import type { Messages } from "./index";
import type { LegalCopy } from "../legal";

/**
 * Srpski, latinica (4b step 3c). Same shape as en.tsx; the type check fails if a text is missing.
 * The member is addressed with "ti", in sentences without grammatical gender (no "zapisao sam"),
 * so no member is addressed as male or female. The three locks keep their own words, as in English:
 *   kapsula je "zapečaćena" do svog vremena,
 *   ključ na uređaju je "zaključan" dok ga PIN ne otvori,
 *   kopija na našem serveru je "blokirana" posle 10 pogrešnih PIN-ova.
 * "Passkey" is "pristupni ključ", as in Google's and Apple's Serbian texts.
 */

/** Serbian plural: 1 kapsula, 2–4 kapsule, 5+ kapsula (11–14 take the last form). */
function pl(n: number, one: string, few: string, many: string): string {
  const d = n % 10;
  const h = n % 100;
  if (d === 1 && h !== 11) return one;
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return few;
  return many;
}

const capsules = (n: number) => `${n} ${pl(n, "kapsula", "kapsule", "kapsula")}`;

export const sr: Messages = {
  pilotAdmin: {
    unassigned: 'Linkovi bez primaoca (bez rezervisanog mesta)',
    invalid: 'Neispravne adrese (neće biti sačuvane)',
    duplicates: 'Ponovljene adrese',
    recover: "Vrati pristup postojećem nalogu",
    recoverGoogle: "Potvrdi novu Google prijavu",
    recoverHelp: "Prvo potvrdi novu adresu za prijavu, pa dokaži postojeći identitet njegovom frazom za oporavak. Koristi se postojeći nalog, čak i kada je pilot pun.",
  "save": "Sačuvaj",
  "users": "Korisnici",
  "settings": "Podešavanja pilota",
  "places": "Zauzeta mesta",
  "remaining": "Slobodno",
  "max": "Maksimalan broj mesta u pilotu",
  "inviteLimit": "Otvorene pozivnice po članu",
  "seatRule": "Predefinisani, aktivni i suspendovani zauzimaju mesto. Ugašeni ga oslobađaju.",
  "inviteRule": "Limit važi za istovremeno otvorene pozivnice. Nula zaustavlja nove pozivnice članova.",
  "ownerOnly": "Samo vlasnik menja limite pilota i otvara registraciju.",
  "bulk": "Predefiniši korisnike",
  "bulkHelp": "Nalepi do 500 email adresa. Svaka nova adresa rezerviše mesto. Ne šalju se emailovi.",
  "preview": "Pregledaj unos",
  "newUsers": "Nove rezervacije",
  "existing": "Već u evidenciji",
  "saved": "Korisnici su sačuvani i mesta rezervisana.",
  "reason": "Unesi razlog (3 do 200 znakova).",
  "confirmAction": "Primeni na izabrane korisnike",
  "activate": "Aktiviraj",
  "suspend": "Suspenduj",
  "deactivate": "Ugasi",
  "referral": "Referral (pozivnica)",
  "hasKey": "Ima ključ",
  "no": "Ne",
  "all": "Svi",
  "byInvite": "Po pozivnici",
  "withoutInvite": "Bez pozivnice",
  "selected": "Izabrano",
  "select": "Izaberi",
  "kind": "Vrsta naloga",
  "human": "Korisnik",
  "services": "Servisni nalozi",
  "role": "Uloga",
  "inviteHelp": "Pozivnica na email odmah rezerviše mesto. Link za nepoznatog primaoca ne rezerviše mesto.",
  "revoked": "Povučena",
  "used": "Iskorišćena",
  "expired": "Istekla",
  "open": "Otvorena",
  "mailed": "Email poslat",
  "notMailed": "Email nije poslat",
  "resend": "Pošalji ponovo",
  "origins": {
    "manual": "Ručno predefinisan",
    "bootstrap": "Prvi vlasnik",
    "open": "Otvorena registracija",
    "invitation": "Pozivnica"
  }
},
  htmlLang: "sr-Latn",
  locale: "sr-Latn-RS",
  langName: "Srpski",
  title: "Vrelis – vremenske kapsule",
  browse: {
    unknownCreated: "Vreme kreiranja nije dostupno na ovom uređaju.",
    filter: "Filter", sort: "Sortiraj po", sender: "Pošiljalac", recipient: "Primalac", subject: "Naslov", group: "Moja grupa", allGroups: "Sve grupe", personPlaceholder: "Ime ili nadimak", created: "Kreirana", received: "Primljena", opens: "Vreme otvaranja", from: "Od (≥)", to: "Do (≤)", asc: "Rastuće", desc: "Opadajuće", reset: "Resetuj", timeNote: "Datumi koriste vremensku zonu uređaja. Od/do uključuju izabrani minut. Primljena je trenutak kada je server dobio kapsulu; vreme pisanja privatne kapsule poznato je samo unutar nje.",
    showMore: "Prikaži još", tooManyPeople: "Više od 50 osoba odgovara; koristi se samo prvih 50. Ukucaj više od imena.", tooShort: "Ukucaj bar 2 slova imena.", senderFilterOff: "Tvoje liste prikazuju pošiljaoca tek posle klika, pa Primljeno nema filter po pošiljaocu ni grupi.", senderUnchecked: (n: number) => `Još ${n} ${n % 10 === 1 && n % 100 !== 11 ? "kapsula iz ovog filtera nije prikazana" : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? "kapsule iz ovog filtera nisu prikazane" : "kapsula iz ovog filtera nije prikazano"}: pošiljalac se vidi tek posle klika, nije potvrđen ili traži tvoj ključ.`, linked: "Kapsula iz linka",
    archived: "Arhivirano", archive: "Arhiviraj", restore: "Vrati", following: "Pratim", watching: "Čekam", blocked: "Blokirani", search: "Pretraga", searchPlaceholder: "Pretraži osobe ili kapsule", users: "Korisnici", messages: "Kapsule", results: "Rezultati pretrage", searchNote: "Pretraga gleda naslov, uvod javne kapsule, autora javne kapsule i primaoce u Poslatom. Ne pretražuje otvoren tekst niti ko ti je poslao privatnu kapsulu. Arhivirane kapsule su isključene.", matches: (n: number) => `${n} rezultata`, matchesMore: (n: number) => `${n}+ rezultata`, loading: "Učitavam još kapsula…", noResults: "Ništa nije pronađeno. Pretraga gleda predmete, javne uvode, datume i imena; tekst zapečaćene kapsule nikad ne vidi.", openIn: (name: string) => `Pogledaj u ${name}`, invalidRange: "Početak svakog raspona mora biti pre njegovog kraja.",
  },
  design: {
    sortOpeningLatest: "Otvaranje · najkasnije prvo", sortOpeningEarliest: "Otvaranje · najranije prvo", sortReceivedLatest: "Primljene · najnovije prvo", sortReceivedEarliest: "Primljene · najstarije prvo",
    messageState: "Vreme otvaranja", otherLists: "Ostale liste kapsula", clearFilters: "Ukloni filtere",
    budgetWindow: "poslednja 24 h", budgetTotal: "ukupno", budgetDay: "24 h",
    refresh: "Osveži", refreshing: "Osvežavanje…", messageBudget: (used: number, total: number) => `Iskorišćene isporuke: ${used} od ${total}`, budgetNote: "Budžet isporuka u poslednja 24 sata. Isporuke primaocima i tvoje kopije računaju se zasebno.", budgetNoteTotal: "Kapsule koje ovaj servisni nalog može da pošalje ukupno. Isporuke primaocima i kopije broje se posebno.",
    sendPrivate: "Pošalji privatnu kapsulu", allMessages: (name: string) => `Sve kapsule od ${name}`, followToWrite: "Privatnu kapsulu možeš da pošalješ samo osobi koju pratiš.", followingTitle: "Ljudi", followingNote: "Koga pratiš, ko prati tebe i koga blokiraš ili sakrivaš.", block: (name: string) => `Blokiraj: ${name}`, unblock: (name: string) => `Odblokiraj: ${name}`, hide: (name: string) => `Sakrij u Javno: ${name}`, unhide: (name: string) => `Ponovo prikaži u Javno: ${name}`,
    groupsAndPeople: "Grupe i kontakti", editGroups: "Uredi grupe", openGroups: "Otvori moje grupe", hidePeople: "Sakrij grupe i kontakte", showPeople: "Prikaži grupe i kontakte",
    viewPublicMessages: "Pregled javnih kapsula", viewMessages: "Javno", message: "Kapsula", writeTo: (name: string) => `Nova kapsula za ${name}`, clearFilter: "Prikaži sve",
    openedAt: (time: string) => `Otvorena ${time}`, countdown: "Otvara se za", sealedInside: "Poruka je unutra i još je zaključana.", targetUnavailable: "Ovaj primalac više nije dostupan. Izaberi ko može da pročita kapsulu.",
    navigation: "Navigacija", profileSettings: "Profil i podešavanja", options: "Dodatne opcije",
    export: "Preuzimanje kapsula", details: "Detalji", composeNote: "Kapsula, primalac i trenutak za otvaranje.", memberHelp: "O podešavanju članova",
    subjectHint: "Vidljiv pre otvaranja, i Vrelisu, zajedno sa vremenom otvaranja. Privatne informacije napiši u tekstu.",
    opensAt: (time: string) => `Otvara se ${time}`, timingDetails: "O vremenskoj bravi",
    availableSince: (time: string) => `Dostupna od ${time}`,
    memberSearch: "Pretraži ime ili email", membersTab: "Članovi", invitesTab: "Pozivnice", settingsTab: "Podešavanja",
    adminNote: "Upravljaj članovima, pozivnicama i pristupom.", accountNote: "Tvoj profil i podešavanja, na jednom mestu.",
    messageActions: "Opcije kapsule", untitled: "Kapsula bez naslova", noMembers: "Nema članova koji odgovaraju pretrazi.",
  },

  common: {
    continue: "Nastavi",
    back: "Nazad",
    cancel: "Otkaži",
    finish: "Završi",
    open: "Otvori",
    opening: "Otvaram…",
    download: "Preuzmi",
    downloading: "Preuzimam…",
    copy: "kopiraj",
    remove: "ukloni",
    loading: "Učitavam…",
    loadingSlow: "Ovo traje duže nego obično. Proveri vezu.",
    tryAgain: "Pokušaj ponovo",
    pin: "PIN",
    repeatPin: "Ponovi PIN",
    recoveryPhrase: "Fraza za oporavak",
    waitingPasskey: "Čekam pristupni ključ…",
    noWindowHint:
      "Ne vidiš prozor za pristupni ključ? Možda je iza ovog prozora ili čeka na traci zadataka; možda browser ili menadžer lozinki traži ažuriranje. Zatim pokušaj ponovo.",
    forgotPin: "Zaboravljen PIN? Upotrebi frazu za oporavak.",
    you: "ti",
    unknown: "nepoznat",
    minutes: (n: number) => `${n} min`,
    more: "Više",
  },

  time: {
    units: { y: "g", d: "d", h: "h", min: "min", s: "s" },
  },

  /** Pilot stage: the header badge and the open call on the landing page. */
  tryIt: {
    title: "Probaj",
    lead: (minutes: number) => `Vođena prva kapsula, a ne uobičajen rad sastavljača: napiši sebi nekoliko reči, zapečati ih na oko ${minutes} minuta i gledaj kako brava drži, pa se otvara. Prava kapsula može da ostane zapečaćena godinama.`,
    subjectSuggestion: "Moja prva kapsula",
    textLabel: "Tvoj tekst",
    textPlaceholder: "Nekoliko reči za sebe za deset minuta.",
    toYou: (minutes: number) => `Tebi. Otvara se za oko ${minutes} minuta; do tada niko ne može da je pročita, ni ti ni Vrelis.`,
    seal: "Zapečati",
    skip: "Preskoči, napiši pravu kapsulu",
    sealedTitle: "Zapečaćeno.",
    opensIn: (left: string, at: string) => `Otvara se za ${left}, u ${at}. Ostani na ovoj stranici ili se vrati kasnije: kapsula je i u listi Poslato.`,
    readyTitle: "Došlo je vreme.",
    open: "Otvori",
    openedTitle: "Otvoreno.",
    openedNote: "Ovo radi svaka kapsula, u trenutku koji izabereš: pre toga niko ne može da je pročita, a posle samo oni kojima je zapečaćena.",
    writeReal: "Napiši pravu kapsulu",
    again: "Probaj ponovo",
    phraseTitle: "Sačuvaj frazu",
    phraseText: "Tvojih 12 reči su tvoj ključ. Ko ih ima, to si ti; bez njih i bez ovog uređaja tvoje privatne kapsule ne mogu da se otvore. Čuvaj ih na papiru, dalje od računara.",
    copyTitle: "Preuzmi kopiju",
    copyText: "Kopija i offline otvarač ti omogućavaju da ovu kapsulu otvoriš frazom i ako vrelis.com nestane. Otvarač traži signal njenog vremena od javnih drand releja.",
    download: "Preuzmi ovu kapsulu",
    opener: "Preuzmi offline otvarač",
    accountLink: "Probaj: vođena prva kapsula",
    accountNote: " Zapečati sebi nekoliko reči na deset minuta i vidi kako se kapsula otvara.",
  },

  pilot: {
    badge: "Pilot faza",
    badgeTitle: "Vrelis je u pilot fazi: testiramo ga sa malom grupom ljudi.",
    noInvite: "Nemaš pozivnicu? Prijavi se za pilot",
    title: "Prijavi se za pilot",
    intro: "Vrelis je u pilot fazi. Pre nego što ga otvorimo za sve, testiramo ga sa malom grupom ljudi, i dobrodošao je svako ko želi da pomogne u testiranju.",
    how: "Da se prijaviš, piši nam i navedi:",
    fields: ["svoju email adresu (na nju šaljemo pozivnicu; za prijavu preko Google-a navedi adresu Google naloga)", "lokaciju: grad i državu", "zanimanje i firmu, ako želiš"],
    use: "Ove podatke koristimo samo da te pozovemo u pilot i da te kontaktiramo u vezi sa njim.",
    backupNote: "Sačuvaj frazu za oporavak i preuzmi kapsule koje su ti važne.",
    button: "Prijavi se mejlom",
    subject: "Vrelis pilot",
    body: "Zdravo,\n\nželim da se pridružim Vrelis pilotu.\n\nEmail:\nLokacija (grad, država):\nZanimanje (nije obavezno):\nFirma (nije obavezno):\n",
  },

  footer: {
    pricing: "Planovi",
    developers: "Za programere",
    theme: "Tema:",
    themes: { system: "Sistem", light: "Svetla", dark: "Tamna" },
    language: "Jezik:",
    translationNote: "Dok se prevodi dopunjavaju, pojedine stranice su još na engleskom.",
    report: "Prijavi problem",
    guide: "Kako Vrelis radi",
    terms: "Uslovi korišćenja",
    privacy: "Privatnost",
    cookies: "Kolačići",
    contact: "O nama i kontakt",
    reportSubject: "Vrelis: problem",
    operator: "Perikle PR, Beograd, Srbija",
    aiAssisted: "Uz pomoć AI-ja",
    aiAssistedTitle: "Vrelis se projektuje i razvija uz pomoć AI alata.",
  },

  legal: {
    updated: "Verzija 1.1 · 23. oktobar 2026. · Besplatni pilot",
    navigation: "Pravne informacije",
    summaryTitle: "Ukratko",
    contact: "Za podršku, pitanja o podacima i prijave zloupotrebe piši na",
    loginNote: "Korišćenje platforme uređuju",
    loginPrivacy: "Kako postupamo sa tvojim podacima:",
    freePilot: "Vrelis je trenutno besplatan pilot koji vodi Perikle PR iz Beograda. Prodaja još nije uključena: ništa se ne naplaćuje i ništa se samo ne obnavlja.",
    sellingLive: "Vrelis vodi Perikle PR iz Beograda. Plaćeni planovi se prodaju preko našeg naplatnog provajdera; cene i šta uključuju su na stranici Planovi.",
    pages: {
      terms: {
        title: "Uslovi korišćenja",
        lead: "Ovde piše šta Vrelis nudi tokom besplatnog pilota, šta je naša odgovornost, a šta tvoja, i gde su granice čuvanja i otvaranja vremenski zaključanih kapsula.",
        summary: [
          "Vrelis je besplatan pilot koji vodi Perikle PR iz Beograda. Ništa se ne plaća i nema automatske naplate.",
          "Kapsula se šifruje u tvom browser-u. Naš server nema čitljiv ključ koji je otvara.",
          "Niko ne može da otvori kapsulu pre njenog vremena: ni mi, ni primalac, ni bilo koji organ.",
          "Ne možemo da povratimo izgubljen ključ. Čuvaj frazu za oporavak i preuzmi ono što ti je važno.",
          "Ne koristi Vrelis za nezakonit sadržaj, uznemiravanje ili spam. Takve naloge možemo da ograničimo.",
        ],
        sections: [
          { heading: "1. Ko vodi Vrelis i šta ovi uslovi pokrivaju", paragraphs: [
            "Vrelis na adresi vrelis.com vodi Perikle PR iz Beograda. Za pitanja, podršku i prigovore piši na adresu na dnu ove stranice. Ovi uslovi važe za sajt, tvoj nalog, API i offline otvarač koji možeš da preuzmeš.",
            "Vrelis razvijamo kao komercijalan softverski proizvod. Za sada ga operator finansira iz sopstvenih sredstava. Pilot je besplatan: nema pretplata, kupovina ni automatskog obnavljanja. Ako jednog dana uvedemo plaćenu uslugu, ponudu i uslove kupovine videćeš pre nego što odlučiš da kupiš. Korišćenje pilota ne znači da pristaješ na neku buduću naplatu.",
            "Obradu podataka posebno opisuju stranice Privatnost i Kolačići. Time što koristiš Vrelis ne daješ opšti pristanak na obrade koje nisu neophodne. Prava koja ti zakon garantuje ostaju netaknuta.",
          ] },
          { heading: "2. Kapsule i vremenska brava", paragraphs: [
            "Vrelis služi za slanje šifrovanih poruka, koje zovemo kapsulama, sa prilozima i vremenom otvaranja koje sam biraš. Javnu kapsulu posle tog vremena može da pročita svako ko ima pristup odeljku Javno. Za privatnu kapsulu potreban je i ključ primaoca. Trenutne mogućnosti i limite opisuju uputstvo i sama aplikacija.",
            "Niko ne može da otvori kapsulu pre njenog vremena: ni mi, ni primalac, ni bilo koji organ, ni iz kog razloga. Naš server nema čitljiv ključ kojim bi je otvorio. Vremenska brava ne zavisi od naše dobre volje, već od javno objavljene kriptografije i mreže drand. Kapsulu koju je neko već preuzeo ne možemo ni da povučemo.",
            "Kada softver radi kako treba, sadržaj se šifruje u tvom browser-u pre slanja. Vremenska brava čeka signal koji mreža drand objavljuje u tačno određenom trenutku. Vreme prikazano uz kapsulu je najraniji trenutak kada ona može da se otvori. To nije obećanje da će tada stići mejl ili da će je primalac tada pročitati.",
            "Bezbednost zavisi od kriptografije, mreže drand, softvera koji ti isporučimo, tvog browser-a i uređaja i od toga kako čuvaš ključeve. Ne obećavamo apsolutnu bezbednost niti da će Vrelis raditi na svakom budućem uređaju i formatu. Potpis pokazuje kojim je ključem kapsula potpisana, ali sam po sebi ne dokazuje ko je osoba iza tog ključa.",
          ] },
          { heading: "3. Ključevi, drand i tvoje kopije", paragraphs: [
            "Čuvaj frazu za oporavak i sopstvenu kopiju važnih kapsula i fajlova. Oporavak naloga ili nova lozinka ne vraćaju izgubljen ključ za šifrovanje. Za privatnu kapsulu poslatu na tvoj raniji ključ možda će ti i dalje trebati taj raniji ključ. Ako izgubiš sve ključeve i sve načine oporavka, sadržaj može ostati trajno nedostupan. Ni podrška nema rezervni ključ kojim bi ga otvorila.",
            "Ako drand nikada ne objavi signal koji kapsula čeka, ona može ostati zauvek zaključana. Preuzimanje šifrovane kapsule unapred ne uklanja taj rizik. Ako je signal objavljen, ali ga nema u tvojoj arhivi, moraćeš da ga nabaviš. To što je datum otvaranja prošao samo po sebi ne znači da tvoja kopija može da se otvori offline.",
            "Kapsula može da se otvori bez interneta ako kompletna arhiva sadrži provereni drand signal i sve potrebne fajlove, a za privatnu kapsulu imaš i odgovarajući ključ. Izvezen otvoren tekst ili fajl je obična čitljiva kopija, bez zaštite šifrovanjem. Kopije koje je neko drugi već preuzeo ne možemo da povučemo.",
          ] },
          { heading: "4. Dostupnost i kraj pilota", paragraphs: [
            "Pilot nema ugovoreni nivo usluge niti obećanje trajnog čuvanja. Mogućnosti i limiti mogu da se menjaju, a održavanje, greške ili ispadi spoljnih servisa mogu privremeno onemogućiti pristup. Ne koristi Vrelis kao jedinu arhivu.",
            "Pilot možemo da pauziramo ili ugasimo. Ako planiramo promenu koja bitno utiče na pristup ili sačuvan sadržaj, potrudićemo se da te obavestimo unapred i da ti ostavimo vreme za izvoz, kad god je to razumno moguće. U hitnom slučaju to možda neće biti moguće. Za sopstvene propuste i protivpravne postupke odgovaramo onako kako zakon nalaže.",
            "Vrelis nije kvalifikovana usluga od poverenja, pravno valjana dostava, depozit kod javnog beležnika, služba za izvršenje testamenta niti sistem za hitne slučajeve. Ne oslanjaj se samo na njega kada su u pitanju pravni rok, zdravlje, bezbednost ili veća imovina.",
          ] },
          { heading: "5. Nalog i dozvoljeno korišćenje", paragraphs: [
            "Unesi tačne podatke i čuvaj svoje načine prijave i uređaje. U ime druge osobe ili firme postupaj samo ako te je za to ovlastila. Ako posumnjaš da je neko preuzeo tvoj nalog, odmah nam javi. Podršci nikad ne šalji lozinku, PIN, frazu za oporavak ili tajni ključ.",
            "Ne koristi Vrelis za nezakonit sadržaj, iskorišćavanje dece, pretnje, uznemiravanje, prevare, kršenje tuđih prava, zlonamerni softver, neovlašćen pristup ili spam. Ne zaobilazi ograničenja, kvote i zaštitne mere i ne ometaj rad servisa. Kapsule i pozivnice šalji samo ljudima kojima imaš opravdan razlog da se javiš.",
            "Nalog možemo da ograničimo, a sadržaj sa našeg servera da uklonimo, kada je to razumno potrebno zbog zloupotrebe, bezbednosnog rizika ili zakonske obaveze. Kada je to primereno i dozvoljeno, objasnićemo ti razlog, a preispitivanje odluke možeš da zatražiš na adresi ispod. Privatne šifrovane kapsule ne možemo da čitamo, pa se oslanjamo na prijave, javni sadržaj i metapodatke koje imamo.",
          ] },
          { heading: "6. Tvoj sadržaj i drugi ljudi", paragraphs: [
            "Sadržaj ostaje tvoj. Dozvoljavaš nam samo onu tehničku obradu, čuvanje i prenos koji su potrebni za funkcije koje koristiš. Time nam ne prenosiš vlasništvo, niti dozvoljavaš da privatni sadržaj koristimo za oglašavanje ili treniranje AI modela.",
            "Ti odgovaraš za to da imaš pravo da pošalješ ili objaviš sadržaj, kao i za izbor primalaca i vremena otvaranja. Javni naslovi, javni uvodi i podaci profila nisu zaštićeni kao sadržaj privatne kapsule. Ni diskretan prikaz ne skriva sve podatke o tome ko kome šalje. Poštuj tuđu privatnost i autorska prava.",
          ] },
          { heading: "7. Odgovornost i prava koja ne mogu da se isključe", paragraphs: [
            "Besplatni pilot nudimo sa funkcijama i tehničkim ograničenjima opisanim na ovoj stranici. Ne garantujemo neprekidan rad, da će primalac uspeti da pročita kapsulu, da se svaka kapsula može povratiti niti da će servis raditi zauvek. Da li i koliko odgovaramo za neki gubitak zavisi od merodavnog prava, konkretne obaveze, uzroka i okolnosti.",
            "Ništa u ovim uslovima ne isključuje niti ograničava našu odgovornost za namerno postupanje, krajnju nepažnju i sve ono što zakon ne dozvoljava da se isključi ili ograniči. To važi i kada je usluga besplatna. Pozivanje na drand ili druge dobavljače ne oslobađa nas automatski naših zakonskih obaveza.",
          ] },
          { heading: "8. Pitanja, merodavno pravo i izmene", paragraphs: [
            "Na ove uslove primenjuje se pravo Republike Srbije. Ako si potrošač, zadržavaš i obaveznu zaštitu koju ti daje pravo zemlje u kojoj živiš. Ništa ovde ne traži da se odrekneš obraćanja nadležnom sudu ili nadzornom organu.",
            "Piši nam ako nešto ne radi, ako je kapsula nestala, ako imaš pitanje o privatnosti ili se ne slažeš sa merom prema nalogu. Navedi oznaku kapsule ili naloga, približno vreme i kratak opis. Ne šalji tajne ključeve ni privatni sadržaj koji nije neophodan. Zahteve rešavamo u skladu sa zakonom.",
            "Uz uslove objavljujemo datum verzije i menjamo ih kada se servis menja. O bitnim izmenama obavestićemo te na odgovarajući način. Ako zakon traži da ponovo prihvatiš uslove ili daš pristanak, sama izmena ove stranice to ne zamenjuje. Budući uslovi naplate neće se primenjivati unazad na besplatno korišćenje.",
          ] },
        ],
      },
      privacy: {
        title: "Obaveštenje o privatnosti",
        lead: "Ovde piše kako Perikle PR iz Beograda obrađuje tvoje podatke o ličnosti dok koristiš besplatni Vrelis pilot. Šifrovanje štiti sadržaj kapsula, ali ne čini anonimnim sve podatke o nalogu i korišćenju.",
        summary: [
          "Čuvamo tvoje ime, email, načine prijave, javni ključ, šifrovane kapsule i metapodatke potrebne za njihovu isporuku.",
          "Privatne kapsule ne možemo da pročitamo. Server ipak vidi ko je kome poslao kapsulu, kada i koliko je velika.",
          "Cloudflare hostuje servis, Resend šalje mejlove, a Google obavlja prijavu preko Google-a.",
          "Ne prodajemo tvoje podatke i ne koristimo tvoje kapsule za oglašavanje ni za treniranje AI modela.",
          "Kopiju, ispravku ili brisanje svojih podataka možeš da tražiš na adresi ispod, i bez naloga.",
        ],
        sections: [
          { heading: "1. Ko obrađuje podatke", paragraphs: [
            "Rukovalac podacima za Vrelis je Perikle PR iz Beograda. Zahteve u vezi sa podacima šalji na adresu ispod, i ako nemaš nalog. Ovo obaveštenje pokriva naloge, kapsule, priloge, API i podršku. U pilotu ne prikupljamo podatke o platnim karticama i nemamo plaćanje na sajtu.",
          ] },
          { heading: "2. Koje podatke imamo i odakle", paragraphs: [
            "Podatke dobijamo od tebe, iz tvog korišćenja servisa, od servisa preko kog se prijavljuješ i od drugih korisnika koji ti pošalju kapsulu ili pozivnicu. Podaci naloga su email, ime koje prikazuješ, podaci profila koje uneseš, interni identifikatori, javni ključevi i načini prijave. Vlasnik i administratori vide podatke naloga, uključujući email adrese, kada je to potrebno za vođenje servisa.",
            "Za prijavu lozinkom ne čuvamo samu lozinku, već zapis izveden iz nje kojim proveravamo da je tačna. Za pristupne ključeve čuvamo podatke za proveru prijave; otisak prsta ili snimak lica kojim otključavaš uređaj nikad ne stiže do Vrelis-a. Google nam pri prijavi daje samo podatke koje odobriš, kao što su identifikator, ime i email. Ne dobijamo pristup tvom Gmail-u ni Google Drive-u.",
            "Podaci servisa su šifrovane kapsule i fajlovi, javni ključevi i potpisi, evidencija slanja, podaci o primaocima, metapodaci javnih kapsula, profil, kontakti, praćenja, blokiranja, arhive i druga podešavanja. Naslovi i uvodi javnih kapsula vidljivi su pre otvaranja. Naslovi i uvodi privatnih kapsula nalaze se u šifrovanom delu. Server i dalje vidi kome se kapsula šalje i kada.",
            "Tehnički podaci su IP adresa, podaci o browser-u i zahtevu, vreme, greške, brojači korišćenja i bezbednosni događaji. Zahtev podršci sadrži tvoj kontakt i tekst i fajlove koje pošalješ. U zahtev ne stavljaj lozinke, frazu za oporavak, tajne ključeve ni osetljive podatke koji nisu neophodni.",
          ] },
          { heading: "3. Šifrovanje i podaci na tvom uređaju", paragraphs: [
            "Kada softver radi kako treba, privatni sadržaj se šifruje na tvom uređaju, a server čuva samo šifrovan oblik i metapodatke potrebne za rad. Kopija ključa koju po tvom izboru čuvamo na serveru takođe je šifrovana. To ne znači da je svaki podatak tajan, niti da operator nikad ne vidi nijedan podatak o ličnosti.",
            "Tvoj uređaj može da čuva kopiju ključa, otvorene kapsule i podešavanja. Otvorene javne kapsule mogu biti sačuvane čitljivo. Otvorene privatne kapsule čuvaju se šifrovane ključem izvedenim iz tvog tajnog ključa. Na uređaju koji ne podržava potrebnu zaštitu kopija ključa može biti sačuvana bez dodatne zaštite pristupnim ključem i PIN-om; aplikacija te na to upozorava pri podešavanju. Stranica Kolačići objašnjava šta se čuva u browser-u i kako to da obrišeš.",
          ] },
          { heading: "4. Zašto obrađujemo podatke i na kom osnovu", paragraphs: [
            "Podatke koristimo da otvorimo nalog i prijavimo te, da pružimo funkcije koje tražiš, da čuvamo i dostavljamo kapsule, da sprovodimo limite i da odgovaramo na zahteve podršci. Kada koristiš servis i obrada je za to neophodna, pravni osnov je izvršenje ugovora sa tobom ili radnje koje tražiš pre zaključenja ugovora.",
            "Bezbednost, sprečavanje zloupotrebe, pouzdan rad, ograničeno merenje performansi i zaštita u eventualnom pravnom sporu mogu se zasnivati na legitimnom interesu. Tada procenjujemo da li je obrada zaista potrebna i da li pretežu tvoja prava. Kada to propis nalaže, podatke obrađujemo radi zakonske obaveze. Za obradu za koju zakon traži pristanak tražimo poseban pristanak; prihvatanje Uslova ga ne zamenjuje.",
            "Ako si primalac ili pozvana osoba, a nemaš ugovor sa nama, ugovor pošiljaoca nije ugovor sa tobom. Podatke potrebne da se kapsula ili pozivnica dostavi i da se spreči zloupotreba obrađujemo na posebnom osnovu, po pravilu na osnovu legitimnog interesa kada je on dozvoljen. Pozivnica nije dozvola da ti šaljemo reklame.",
          ] },
          { heading: "5. Dobavljači i ko još dobija podatke", paragraphs: [
            "Cloudflare isporučuje sajt i API, čuva bazu i fajlove, štiti servis i pruža Web Analytics. Resend šalje mejlove servisa. Google obrađuje prijavu kada je izabereš. Svaki dobavljač obrađuje podatke u svojoj ulozi i po svojim uslovima; nemaju svi istu pravnu ulogu.",
            "Cloudflare Web Analytics meri posete i brzinu sajta. Prema Cloudflare-u, ta usluga ne koristi kolačiće, lokalno skladište ni fingerprinting (prepoznavanje uređaja po njegovim osobinama) da bi prepoznala posetioce, i ne prati pojedince između sajtova svojih klijenata. Browser ipak šalje Cloudflare-u zahteve i tehnička merenja, pa to ne znači da obrade uopšte nema.",
            "Primaoci i drugi korisnici vide ono što im dozvoljavaju vidljivost i deljenje koje izabereš. drand relay (server koji prosleđuje drand signal) vidi tvoju IP adresu i podatke zahteva kada uređaj preuzima signal, ali ne i tekst kapsule. Podatke možemo dati organu kada to zakon nalaže, ili kada je to potrebno i zakonito radi zaštite nečijih prava. Čitljiv sadržaj koji nemamo ne možemo nikome da damo.",
            "Ne prodajemo tvoje podatke za tuđe oglašavanje i ne koristimo privatne kapsule za oglašavanje ni treniranje AI modela. Pravila dobavljača:",
          ], links: [
            { label: "Cloudflare politika privatnosti", href: "https://www.cloudflare.com/privacypolicy/" },
            { label: "Cloudflare Web Analytics", href: "https://developers.cloudflare.com/web-analytics/about/" },
            { label: "Resend politika privatnosti", href: "https://resend.com/legal/privacy-policy" },
            { label: "Google politika privatnosti", href: "https://policies.google.com/privacy" },
          ] },
          { heading: "6. Obrada izvan Srbije", paragraphs: [
            "Operator je u Srbiji, ali dobavljači imaju globalnu infrastrukturu, pa se podaci mogu obrađivati ili biti dostupni i u drugim zemljama. Ne tvrdimo da svi podaci ostaju u Srbiji ili EU. Prenos podataka u druge zemlje mora da bude u skladu sa propisima o zaštiti podataka. Na adresi ispod možeš da pitaš o kojim primaocima, zemljama i merama zaštite je reč.",
          ] },
          { heading: "7. Koliko dugo čuvamo podatke", paragraphs: [
            "Podatke o nalogu i kapsulama čuvamo dok su potrebni za rad pilota, uz poštovanje tvojih prava, zakonskih obaveza i pravila o gašenju pilota. Nemamo fiksan rok posle kog se svaki nalog ili kapsula automatski briše. Skrivanje ili arhiviranje kapsule nije brisanje.",
            "Koliko dugo čuvamo neki zapis zavisi od njegove svrhe: rad servisa, bezbednost naloga, otvoren zahtev podršci, sprečavanje zloupotrebe, zakonska obaveza ili rok za eventualni pravni spor. Kada svrha prestane, podatke brišemo ili anonimizujemo, osim kada zakon ili prava drugih korisnika nalažu drugačije. Ako želiš da obrišemo tvoje podatke, javi nam se; deaktivacija naloga sama po sebi ne briše sve zapise.",
            "Servis redovno i automatski briše istekle email tokene i stare privremene bezbednosne zapise i brojače za slanje mejlova. Logovi hostinga, kopije kod dobavljača i rezervne kopije imaju svoje rokove. Fajlove koje si ti ili primalac preuzeo ne kontrolišemo; brisanje naloga ih ne briše. Kako da obrišeš podatke iz browser-a, piše na stranici Kolačići.",
          ] },
          { heading: "8. Pozivnice i ljudi bez naloga", paragraphs: [
            "Korisnik može da unese tvoj email da bi te pozvao ili ti poslao kapsulu. Tada čuvamo adresu, status pozivnice ili dostave i potrebne bezbednosne zapise. Taj podatak smo dobili od korisnika koji ti se obratio. Možeš nam pisati i bez registracije: da pitaš koje podatke imamo, da tražiš da ti više ne stižu pozivnice ili da zatražiš brisanje.",
          ] },
          { heading: "9. Tvoja prava, pritužbe i bezbednost", paragraphs: [
            "Pod uslovima iz zakona možeš da tražiš uvid u svoje podatke i njihovu kopiju, ispravku, brisanje, ograničenje obrade ili prenos podataka. Možeš da uložiš prigovor na obradu zasnovanu na legitimnom interesu i da povučeš pristanak ako si ga dao. Tvoj identitet možemo da proverimo u meri u kojoj je to potrebno, ali nikad tražeći tajni ključ ili frazu za oporavak. Odgovaramo u zakonskom roku, po srpskom zakonu po pravilu u roku od 30 dana; ako rok moramo da produžimo, obavestićemo te.",
            "Pritužbu možeš da podneseš Povereniku za informacije od javnog značaja i zaštitu podataka o ličnosti ili drugom nadležnom organu. Ne moraš prvo da se obratiš nama. Podatke štitimo merama primerenim riziku, a o incidentima obaveštavamo kako zakon nalaže. Šifrovanje ne znači da incident nije moguć.",
            "Automatske provere kvota i bezbednosti štite servis. Ako te provera greškom ograniči, traži da odluku preispitamo. Privatne kapsule ne koristimo za pravljenje reklamnih profila. Pilot nije namenjen deci i ne traži osetljive podatke; ako primetiš problem sa nalogom ili podacima, javi nam.",
          ], links: [{ label: "Poverenik Republike Srbije", href: "https://www.poverenik.rs" }] },
          { heading: "10. Izmene", paragraphs: [
            "Obaveštenje menjamo kada se promene obrada ili propisi, a o bitnim izmenama obaveštavamo na odgovarajući način. Izmena teksta ne zamenjuje novi pristanak kada je on potreban. Ako uvedemo plaćanje, opisaćemo ga ovde pre nego što počne da radi.",
          ] },
        ],
      },
      cookies: {
        title: "Kolačići i podaci u browser-u",
        lead: "Vrelis koristi nekoliko kolačića i zapisa u browser-u za prijavu, podešavanja i šifrovane kapsule. Local Storage i IndexedDB su mesta za čuvanje podataka u browser-u, ali nisu kolačići.",
        summary: [
          "Jedan kolačić te drži prijavljenim do 7 dana; drugi 10 minuta štiti prijavu preko Google-a.",
          "Tvoj browser čuva jezik, temu, ključ i otvorene kapsule.",
          "Cloudflare Web Analytics broji posete bez kolačića. Nema reklamnih piksela.",
          "Pre brisanja podataka iz browser-a proveri da imaš frazu za oporavak.",
        ],
        sections: [
          { heading: "1. Kolačići za prijavu", paragraphs: [
            "tc_session te drži prijavljenim najduže sedam dana od prijave, osim ako se sesija ranije završi ili poništi. Na sajtu ga štite podešavanja Secure, HttpOnly i SameSite=Lax. Ne sadrži sadržaj kapsula ni tvoj tajni ključ.",
            "tc_oauth štiti prijavu preko Google-a i traje najviše deset minuta. Oba kolačića služe samo za prijavu i bezbednost. Ako ih blokiraš, prijava neće raditi. Kolačiće neophodne za uslugu koju tražiš razlikujemo od neobaveznih, za koje je potreban pristanak.",
          ] },
          { heading: "2. Podešavanja i podaci na uređaju", paragraphs: [
            "U Local Storage zapisi vrelis-lang i vrelis-theme pamte jezik i temu dok ih ne promeniš ili obrišeš. Zapis timecapsule.keyStepSkipped pamti, posebno za svakog člana, da je u ovom browser-u preskočen korak podešavanja ključa. Ovi zapisi služe samo radu aplikacije i ne koriste se za oglašavanje.",
            "U IndexedDB bazi timecapsule, u skladištu kv, nalaze se lokalna kopija ključa, otvorene kapsule i podaci o fajlovima, beleške o slanju i ranije viđeni javni ključevi. Otvorene privatne kapsule su šifrovane; javne mogu biti čitljive. Na uređaju čiji pristupni ključ ne podržava PRF (proširenje koje omogućava dodatno šifrovanje) kopija ključa može biti bez tog dodatnog sloja zaštite. Zato čuvaj pristup svom uređaju.",
          ] },
          { heading: "3. Analitika i spoljni servisi", paragraphs: [
            "Sajt koristi Cloudflare Web Analytics za merenje poseta i brzine. Cloudflare navodi da Web Analytics ne koristi kolačiće, lokalno skladište ni fingerprinting za prepoznavanje posetilaca. Zahteve za merenje i ostalu obradu za hosting i bezbednost opisuje stranica Privatnost.",
            "Prijava preko Google-a otvara Google-ov servis, koji može koristiti svoje kolačiće po svojim pravilima. Pilot nema plaćanje na sajtu ni reklamne piksele. Neobavezne tehnologije za koje je potreban pristanak ne uključujemo bez tvog pristanka. Ako ih jednog dana uvedemo, prvo ćemo izmeniti ovu stranicu i, kada je potrebno, dati ti izbor.",
          ] },
          { heading: "4. Kako da promeniš ili obrišeš podatke", paragraphs: [
            "Temu i jezik menjaš u aplikaciji, a kolačiće i podatke sajta možeš da pregledaš i obrišeš u podešavanjima browser-a. Dugme Zaboravi ovaj uređaj briše iz browser-a ono što je Vrelis sačuvao za trenutni nalog. Ne briše nalog na serveru, podatke drugih naloga ni arhive koje si preuzeo.",
            "Pre brisanja podataka iz browser-a proveri da ključ možeš da vratiš na drugi način. Ako izgubiš poslednju upotrebljivu kopiju ključa, privatne kapsule mogu ostati zauvek nedostupne. Odjava, zaključavanje ključa i brisanje podataka iz browser-a nisu isto. Oporavak i izvoz objašnjava uputstvo.",
          ] },
        ],
      },
      contact: {
        title: "O Vrelis-u i kontakt",
        lead: "Vrelis je softver za šifrovane poruke koje se otvaraju tek u vreme koje izabereš; te poruke zovemo kapsulama. Besplatni pilot vodi Perikle PR iz Beograda.",
        summary: [
          "Vrelis je besplatan pilot koji vodi Perikle PR iz Beograda, a finansira ga operator iz sopstvenih sredstava.",
          "Jedna adresa prima pitanja za podršku, zahteve u vezi sa podacima i prijave zloupotrebe. Nalog nije potreban.",
          "Nikada nam ne šalji lozinku, PIN, frazu za oporavak ili tajni ključ.",
        ],
        sections: [
          { heading: "1. Ko stoji iza Vrelis-a", paragraphs: [
            "Operator: Perikle PR, Beograd, Republika Srbija. Sajt: vrelis.com. Email: support@vrelis.com; na istu adresu vodi i veza Prijavi problem u podnožju sajta.",
            "Razvoj i servere za sada plaća operator iz sopstvenih sredstava. Vrelis je komercijalan softverski projekat sa besplatnim pilotom: nema kupovina, pretplata ni automatske naplate. Šta Vrelis trenutno ume, objašnjava uputstvo; dostupnost i tehnička ograničenja opisuju Uslovi korišćenja.",
          ] },
          { heading: "2. Podrška i prijava problema", paragraphs: [
            "Pošalji kratak opis, oznaku kapsule ako je imaš, približno vreme i koji browser ili uređaj koristiš. Ne šalji lozinku, PIN, frazu za oporavak ni tajni ključ. Podrška ne radi non-stop i nemamo ugovoreni rok za odgovor. Vrelis nije služba za hitne slučajeve.",
          ] },
          { heading: "3. Privatnost i zloupotrebe", paragraphs: [
            "Na istu adresu šalji pitanja o podacima o ličnosti, neželjenim pozivnicama, nezakonitom sadržaju i merama prema nalogu. Nalog nije potreban. Napiši dovoljno da možemo da pronađemo problem, ali ne prosleđuj privatni ili nezakonit sadržaj ako to nije neophodno. Tvoja prava objašnjava stranica Privatnost, a pravila korišćenja i preispitivanje mera Uslovi korišćenja.",
          ] },
          { heading: "4. Sačuvaj svoju kopiju", paragraphs: [
            "Sačuvaj frazu za oporavak i izvezi sadržaj koji želiš da zadržiš. Vrelis ne obećava večno čuvanje. Izvezena kapsula se i dalje otvara samo uz svoje fajlove, odgovarajući drand signal i, za privatnu kapsulu, tvoj ključ. Detalje daju Uslovi korišćenja i uputstvo.",
          ] },
        ],
      },
    },
  } satisfies LegalCopy,

  app: {
    cannotReach: (detail: string) => `Server nije dostupan: ${detail}`,
    nav: { feed: "Javno", inbox: "Primljeno", sent: "Poslato", watching: "Čekam", correspondence: "Prepiska", write: "Nova kapsula", admin: "Admin" },
    keyMismatch: "Ključ na ovom uređaju nije trenutni identitet tvog naloga. ",
    noKey: "Još nemaš ključ: možeš da čitaš javne kapsule, ali ne i da pišeš ili primaš privatne. ",
    noKeyLink: "Napravi ključ pod Ključevi",
    keyMissing: "Tvoj ključ nije na ovom uređaju, pa ovde ne možeš da pišeš ni da otvaraš privatne kapsule. ",
    setUpLink: "Postavi ga pristupnim ključem ili frazom za oporavak",
  },

  login: {
    lead: "Napiši danas poruku koju niko ne može da pročita pre datuma koji izabereš. Ni primalac, ni mi, ni ti.",
    heroTitle: "Poruke zapečaćene vremenom",
    story: {
      eyebrow: "O tajnama i vremenu",
      title: "I tajne imaju svoje vreme.",
      myth: "Kralj Mida skrivao je magareće uši. Njegov berberin šapnuo je tajnu zemlji, ali iz nje je izrasla trska. Kad bi vetar zaduvao, trska bi je odala.",
      reflection: "Možda nas ta priča prati zato što znamo koliko je tanka granica između potrebe da nešto sačuvamo i želje da to podelimo. Neke reči čekaju hrabrost. Neke pravu osobu. Neke samo svoje vreme.",
      closing: "Vremenska kapsula čuva taj trenutak. Ti biraš kada tajna može da postane priča.",
    },
    features: {
      label: "Šta možeš sa Vrelis-om?",
      carousel: "karusel",
      choose: "Izaberi funkcionalnost",
      previous: "Prethodna funkcionalnost",
      next: "Sledeća funkcionalnost",
      more: "Kako radi i za koga je",
      forWhom: "Za koga je?",
      example: "Na primer:",
      sealedReply: {
        motto: "Zapiši svoj stav. Kovertiraj odgovor. Otvori kada dođe vreme.",
        explanation: "Kao kod odgovora u zapečaćenoj koverti: svako beleži svoje mišljenje pre nego što pročita tuđe. Odgovori se otvaraju osobi koja pita, a ona bira da li će ih kasnije podeliti sa drugima.",
        uses: [
          { label: "Nezavisni stavovi", text: "Prvo svako svoj odgovor. Tek onda zajednička diskusija." },
          { label: "Procene i ponude", text: "Zabeleži danas. Uporedi u dogovoreno vreme." },
          { label: "Predviđanja", text: "Predvidi sada. Pusti budućnost da pokaže." },
        ],
      },
      slides: {
        sase: {
          eyebrow: "Traži odgovor",
          title: "Odgovori sada. Otkrij kasnije.",
          description: "Postavi isto pitanje nekolicini ljudi. Svako odgovara privatno, a niko, ni ti, ne može da pročita odgovore pre datuma koji izabereš.",
          how: "Napiši privatnu kapsulu i štikliraj „Traži odgovor”. Prvi odgovor svakog primaoca plaćaš unapred, jednom markom po osobi, pa odgovor njih ništa ne košta. Odgovori stižu samo tebi i ostaju zapečaćeni do vremena otvaranja koje odrediš, ili onog koje svaki primalac izabere; primaoci ne vide tuđe odgovore. Običan odgovor čitaš čim stigne; ovaj ne, pa nijedan odgovor ne može da se povede za ranijim. U vodiču se ovo zove SASE, po koverti sa adresom i plaćenom poštarinom za odgovor.",
          audience: "Za prijatelje koji skupljaju predviđanja, porodice koje čuvaju uspomene i timove koji žele nezavisne ideje pre čitanja odgovora.",
          example: "Petoro prijatelja, jedno pitanje u januaru: „Gde ćemo biti za sledeću Novu godinu?” Svako odgovara za sebe; 31. decembra otvarate svih pet odgovora zajedno, a niko nije mogao da proviri ranije.",
        },
        future: {
          eyebrow: "Poruke u budućnost",
          title: "Neke reči čekaju pravi trenutak.",
          description: "Pismo budućem sebi, rođendansko iznenađenje ili uspomena za dragu osobu. Napiši danas, a izaberi kada poruka može da se otvori.",
          how: "Izaberi primaoca i datum otvaranja. Poruka i prilozi šifruju se u tvom browser-u. Javni vremenski servis drand omogućava otvaranje tek kada taj trenutak dođe — ni Vrelis ne može da je otvori ranije.",
          audience: "Za svakoga ko želi da sačuva misao, obeleži važan datum ili pošalje iznenađenje koje čuva svoju tajnu.",
          example: "Napiši sebi pismo danas i otvori ga na sledeći rođendan.",
        },
        group: {
          eyebrow: "Grupe i CC",
          title: "Jedan trenutak. Tvoji ljudi.",
          description: "Pošalji zapečaćenu poruku većem broju ljudi odjednom. Izaberi privatno grupno slanje ili CC kada svi treba da vide ko je još dobio poruku.",
          how: "Izaberi ljude pojedinačno ili iz grupa u svom adresaru. Svako dobija privatnu kapsulu. Uz CC, spisak primalaca vidi se unutar zapečaćene poruke; bez CC-a, primaoci ne vide ko je još dobio poruku. Možeš dodati i SASE povratnicu.",
          audience: "Za porodice, prijatelje i timove koji dele uspomenu, plan ili pitanje sa istim datumom otvaranja.",
          example: "Pošalji prijateljima uspomenu sa okupljanja, zapečaćenu do vašeg sledećeg susreta.",
        },
        public: {
          eyebrow: "Privatno ili javno",
          title: "Za nekog posebnog. Ili za sve.",
          description: "Sačuvaj kapsulu između sebe i primaoca ili je objavi za sve u Vrelis-u. U oba slučaja, vreme čuva njen sadržaj.",
          how: "Privatne kapsule šifrovane su za izabrane primaoce. Javne kapsule mogu da pročitaju svi u pilotu kada dođe vreme. Obe mogu imati priloge. Javnoj kapsuli možeš dodati i kratak uvod koji se vidi pre otvaranja.",
          audience: "Za ljude koji čuvaju lične uspomene i autore koji žele da kasnije podele predviđanje, priču ili ideju sa zajednicom.",
          example: "Objavi predviđanje danas. Neka ga svi pročitaju kada se godina završi.",
        },
      },
    },
    heroPoints: [
      "Vremensku bravu daje javni izvor slučajnosti (drand): ključ za čitanje kapsule postoji tek od trenutka koji izabereš.",
      "Javne kapsule za sve u pilotu; privatne za ljude koje pratiš ili za nekoga ko još nije član.",
      "Tvoj ključ nastaje i ostaje u tvom browser-u. Naš server čuva samo zapečaćene kapsule i nema ključ kojim bi ih otvorio.",
    ],
    trustLink: "Zašto bravi može da se veruje",
    steps: {
      title: "Kako radi",
      items: [
        "Napiši pismo, belešku ili uspomenu, i dodaj fajlove ako želiš.",
        "Izaberi ko može da je pročita i kada se otvara: za pet minuta ili za dvadeset godina.",
        "Zapečati se u tvom pregledaču pre nego što ode. Do tog trenutka niko ne može da je pročita: ni primalac, ni Vrelis, ni ti.",
      ],
    },
    signInTitle: "Prijava",
    invite: "Imaš pozivnicu. Prijavi se Google nalogom na koji je poslata, ili se ispod registruj tom adresom.",
    google: "Nastavi preko Google-a",
    privacy: "Google nam kaže samo tvoje ime i email adresu. Tvoj ključ se pravi i čuva u tvom browser-u; Vrelis ga nikad ne vidi.",
    guide: "Kako Vrelis radi",
    errors: {
      invite_required: "Za ovaj pilot potrebna je odobrena email adresa ili važeća pozivnica. Registruj se odobrenom adresom ili otvori link iz pozivnice.",
      invite_invalid: "Ova pozivnica ne važi: već je iskorišćena, istekla je ili je za drugu email adresu.",
      member_suspended: "Nalog je suspendovan. Obratite se administratoru: support@vrelis.com.",
    status_conflict: "Korisnik je u međuvremenu izmenjen. Osveži i pokušaj ponovo.",
    owner_only: "Samo Owner može menjati ova podešavanja.",
    target_self: "Ne možeš blokirati sopstveni nalog.",
    pilot_full: "Sva mesta u pilotu su zauzeta. Obratite se administratoru.",
    member_deactivated: "Administrator je ukinuo tvoj pristup ovom pilotu. Ako misliš da je greška, javi se administratoru.",
      state_mismatch: "Prijava je trajala predugo ili je započeta u drugoj kartici. Pokušaj ponovo.",
      google: "Google nije potvrdio prijavu. Pokušaj ponovo.",
      other: (code: string) => `Prijava nije uspela (${code}).`,
    },
    passkey: {
      button: "Prijavi se pristupnim ključem",
      busy: "Čeka se pristupni ključ…",
    },
    password: {
      modes: { signin: "Email i lozinka", register: "Registracija" },
      email: "Email",
      name: "Ime (nije obavezno)",
      password: "Lozinka",
      newPassword: "Nova lozinka",
      repeat: "Ponovi lozinku",
      rule: "Najmanje 10 znakova.",
      differ: "Lozinke se razlikuju.",
      busy: "Radim…",
      submit: { signin: "Prijavi se", register: "Registruj se", reset: "Pošalji link za novu lozinku" },
      forgot: "Zaboravljena lozinka?",
      registerNote: "Šaljemo link na ovu adresu; nalog postoji kad ga otvoriš. Lozinka služi samo za prijavu: tvoj ključ i dalje čuvaju pristupni ključ i PIN.",
      checkMail: (email: string) => `Poslali smo link na ${email}. Otvori ga u roku od sat vremena da završiš.`,
      resetText: "Upiši adresu svoje lozinke. Ako za nju postoji lozinka, šaljemo link za novu.",
      resetSent: "Ako za ovu adresu postoji lozinka, link je na putu. Važi sat vremena.",
    },
  },

  verify: {
    title: { recover: "Potvrdi novu adresu za prijavu", register: "Potvrdi adresu", add: "Potvrdi novu lozinku", reset: "Postavi novu lozinku" },
    text: {
      register: (email: string) => `Potvrdi ${email} da se napravi nalog i da se prijaviš.`,
      recover: (email: string) => `Potvrdi ${email}, pa dokaži postojeći identitet njegovom frazom za oporavak.`,
      add: (email: string) => `Potvrdi ${email} da nalogu dodaš prijavu lozinkom.`,
      reset: (email: string) => `Izaberi novu lozinku za ${email}. Sve ostale sesije naloga se završavaju.`,
    },
    confirm: { recover: "Potvrdi i nastavi oporavak", register: "Potvrdi i prijavi se", add: "Potvrdi", reset: "Sačuvaj i prijavi se" },
    home: "Nazad na Vrelis",
  },
  unsubscribe: {
    title: "Isključi ove mejlove",
    question: (kind: string) => `Da isključiš mejlove ove vrste: ${kind}?`,
    confirm: "Isključi",
    done: (kind: string) => `Gotovo: mejlove ove vrste više ne dobijaš: ${kind}. Ponovo ih uključuješ u Nalogu.`,
    settings: "Podešavanja obaveštenja u Nalogu",
  },

  onboarding: {
    welcome: (name: string) => `Zdravo, ${name}`,
    needKey: "Pre nego što bilo šta napišeš, treba ti tvoj lični ključ.",
    googleRole: (
      <>
        <b>Prijava</b> (Google, lozinka ili pristupni ključ) samo otvara tvoj nalog.
      </>
    ),
    keyRole: (
      <>
        <b>Tvoj ključ</b> je tvoj identitet: potpisuje tvoje kapsule i otvara privatne kapsule poslate tebi. Pravi se u ovom browser-u.
      </>
    ),
    phraseRole: (
      <>
        <b>Tvoja fraza za oporavak</b> je 12 reči koje ponovo grade ključ na bilo kom uređaju, i za 20 godina, i ako ovaj sajt više ne postoji. Zapisuješ je na
        papir; mi je nikad ne vidimo.
      </>
    ),
    createKey: "Napravi moj ključ",
    havePhrase: "Već imam frazu za oporavak",
    phraseTitle: "Tvoja fraza za oporavak",
    typePhrase: "Upiši 12 reči redom. Ostaju u ovom browser-u.",
    phraseIs: (npub: string) => `Ova fraza je identitet ${npub}.`,
    relinkText: (owner: string) => (
      <>
        Ovaj identitet pripada članu <b>{owner}</b>. Ako si to ti i više ne možeš da se prijaviš na stari način, prebaci ga na ovu prijavu. Član zadržava ovaj
        način prijave; stara prijava iste vrste (Google ili lozinka) prestaje da radi, ostale ostaju.
      </>
    ),
    relink: "Prebaci moj identitet na ovu prijavu",
    relinking: "Prebacujem…",
    writeDown: "Zapiši ovih 12 reči na papir, ovim redom, i čuvaj papir na sigurnom mestu. Nemoj da ih fotografišeš ni da ih čuvaš na računaru.",
    whoeverHas: (
      <>
        Ko ima ove reči, <b>jeste</b> ti: može da potpisuje kao ti i da otvara tvoje privatne kapsule. Ako izgubiš reči i sve uređaje sa ključem, tvoje privatne
        kapsule se nikad ne mogu otvoriti. Mi ne možemo da ih povratimo.
      </>
    ),
    wrote: "Svih 12 reči je zapisano na papiru.",
    checkTitle: "Proveri papir",
    checkText: "Upiši ove reči sa papira.",
    word: (n: number) => `Reč ${n}`,
    showAgain: "Pokaži reči ponovo",
  },

  keySetup: {
    pinRule: (min: number) => `Samo cifre, najmanje ${min}.`,
    pinsDiffer: "Dva PIN-a se razlikuju.",
    wordCount: (n: number) => `${n} od 12 reči.`,
    unknownWord: (n: number, word: string) => `Reč ${n}, „${word}”, nije reč za oporavak. Proveri kako je napisana.`,
    checksum: "Sve reči su poznate, ali ne čine ispravnu frazu. Proveri redosled.",
    title: "Zaštiti ključ na ovom uređaju",
    saving: "Čuvam tvoj ključ…",
    switchNeedsKey: "Za prelazak na ovaj identitet treba ti trenutni ključ na ovom uređaju. Ako ga više nemaš, zamoli administratora da poništi tvoj identitet.",
    switchPurpose: "da potvrdiš prelazak trenutnim ključem",
    passkeyName: (name: string) => `Vrelis – ${name}`,
    noPrf: "Pristupni ključ je napravljen, ali ovaj browser ili uređaj ne može iz njega da izvede ključeve (nema podršku za PRF).",
    switchText: (next: string, current: string) =>
      `Ovim ${next} postaje tvoj identitet umesto ${current}. Kapsule potpisane trenutnim identitetom ostaju pripisane njemu.`,
    switchPrivate:
      "Privatne kapsule poslate tvom trenutnom identitetu ostaju zapečaćene za njega: neće se otvoriti pod novim. Sačuvaj trenutnu frazu za oporavak da ih kasnije otvoriš. Članovi koji ti pišu videće da se tvoj ključ promenio.",
    understand: "Razumem",
    choose: (serverCopy: boolean) =>
      `Izaberi PIN i napravi pristupni ključ (otisak prsta, lice ili PIN uređaja). Zajedno zaključavaju ključ na ovom uređaju${serverCopy ? " i kopiju koju čuvamo za prelazak na novi uređaj" : ""}. Mi nikad ne vidimo ni PIN ni ključ.`,
    pinProtects: (serverCopy: boolean) =>
      serverCopy
        ? "PIN štiti našu kopiju od pogađanja: posle 10 pogrešnih PIN-ova ona je blokirana, a deblokira je samo tvoja fraza. Na ovom uređaju PIN zaustavlja samo slučajnog lopova: ko može da prođe i proveru tvog pristupnog ključa, može da proba sve PIN-ove."
        : "PIN štiti ključ na ovom uređaju. Zaustavlja samo slučajnog lopova: ko može da prođe i proveru tvog pristupnog ključa, može da proba sve PIN-ove.",
    createPasskey: "Napravi pristupni ključ i završi",
    withoutPasskeyText: (
      <>
        Možeš da nastaviš i bez pristupnog ključa. Ključ se tada čuva <b>nešifrovan</b> u ovom browser-u: svako ko može da koristi ovaj profil browser-a može da
        koristi i tvoj identitet. Na serveru ne čuvamo <b>nikakvu kopiju</b>: za drugi uređaj upiši tamo frazu za oporavak.
      </>
    ),
    withoutPasskey: "Nastavi bez pristupnog ključa",
  },

  restore: {
    welcomeBack: (name: string) => `Opet zdravo, ${name}`,
    relinked: (npub: string) => `Ova Google prijava sada otvara tvoj identitet ${npub}. Zaštiti ključ na ovom uređaju.`,
    title: "Postavi ključ na ovom uređaju",
    identity: (npub: string) => `Tvoj identitet: ${npub}`,
    blocked: "Kopija na našem serveru je blokirana posle 10 pogrešnih PIN-ova. Upotrebi frazu za oporavak; ona upisuje novu kopiju i deblokira je.",
    noPasskey: "Na ovom uređaju nema pristupnog ključa, ili je PIN zaboravljen?",
    usePhrase: "Upotrebi frazu za oporavak",
    usePasskeyInstead: "Upotrebi ipak pristupni ključ i PIN",
    passkeyText:
      "Tvoj pristupni ključ (sinhronizovan sa drugog uređaja, ili na telefonu) i tvoj PIN otvaraju kopiju tvog ključa koju čuvamo. Mi sami ne možemo da je otvorimo.",
    attemptsLeft: (n: number) => `Još ${n} ${pl(n, "pokušaj", "pokušaja", "pokušaja")} pre nego što kopija bude blokirana.`,
    usePasskey: "Upotrebi pristupni ključ i PIN",
    blockedShort: "Kopija na serveru je blokirana. Upotrebi frazu za oporavak.",
    phraseText: "Upiši 12 reči svoje fraze za oporavak. One ovde, u browser-u, ponovo grade tvoj ključ i nikad nam se ne šalju.",
    otherMember: (name: string) => `Ova fraza je identitet drugog člana (${name}). Ne može se koristiti sa ovim nalogom.`,
    otherIdentity: (npub: string) =>
      `Ova fraza je drugi identitet (${npub}) od onog koji tvoj nalog sada koristi. Za prelazak treba ti trenutni ključ na ovom uređaju; ako ga više nemaš, zamoli administratora da poništi tvoj identitet.`,
    useThis: "Koristi ovaj identitet",
  },

  keyBar: {
    unlockedUntil: (time: string | null) => `Tvoj ključ je otključan u ovom browser-u${time ? ` do ${time}` : ""}.`,
    lockNow: "Zaključaj sada",
    locked: "Tvoj ključ je zaključan. Vrelis traži PIN kad otvaraš ili šalješ nešto privatno. Za čitanje više kapsula zaredom otključaj ga na neko vreme:",
    unlockFor: (minutes: number) => `Otključaj na ${minutes} min`,
    lock: "Sakrij otvorene",
    lockTitle: "Sakriva svaku privatnu kapsulu otvorenu na ovoj stranici",
    wrongPin: "Pogrešan PIN.",
  },

  keyDialog: {
    title: "Tvoj PIN",
    text: (purpose: string) => `Vrelis-u treba tvoj ključ ${purpose}. Upiši PIN, pa potvrdi pristupnim ključem. Ključ se otvara samo u ovom browser-u.`,
    keep: "Ostavi ključ otključan",
    minutes: "min",
    purposes: {
      open: "da otvori ovu kapsulu",
      send: "da potpiše i pošalje ovu kapsulu",
      profile: "da potpiše tvoj profil",
      download: "da preuzme ovu kapsulu",
      downloadMany: "da preuzme privatne kapsule",
      reset: "da potvrdi poništavanje identiteta",
      role: "da potvrdi promenu uloge",
    },
  },

  sealedNotice: {
    title: "Tvoja kapsula je zapečaćena i sačuvana.",
    opens: (time: string) => `Otvara se ${time}. Do tada niko ne može da pročita šta je unutra, ni ti.`,
    everyone: "Tada je mogu pročitati svi u pilotu.",
    self: "Tada je možeš pročitati samo ti.",
    people: (n: number) => `Primalaca: ${n}.`,
    copyNote: "Sačuvaj svoju kopiju: otvara se offline otvaračem i bez vrelis.com.",
    download: "Preuzmi kopiju",
    filesNote: "Za kopiju sa fajlovima preuzmi kapsulu sa spiska ispod.",
    close: "Zatvori",
  },

  compose: {
    summaryTitle: "Pre pečaćenja",
    summaryTo: "Ko može da pročita",
    summaryOpens: "Otvara se",
    summaryReplies: "Odgovori se otvaraju",
    summaryVisiblePublic: "Svi vide pre otvaranja",
    summaryVisiblePrivate: "Primaoci i Vrelis vide pre otvaranja",
    summarySealed: "Zapečaćeno do otvaranja",
    summaryText: (chars: string) => `Tekst: ${chars} znakova`,
    summaryFiles: (n: string) => `Fajlovi: ${n}`,
    summaryCost: "Cena",
    summaryFree: "Bez maraka",
    summaryEveryone: "Svi u pilotu: javna kapsula",
    summarySelf: "Samo ti",
    summaryNewcomer: (email: string) => `${email}, još nije član`,
    summaryMore: (n: number) => `+${n}`,
    draftRestored: "Tvoj neposlati nacrt je vraćen.",
    draftDiscard: "Odbaci nacrt",
    draftDiscardConfirm: "Odbaciti ovaj nacrt? Tekst i fajlovi ne mogu da se vrate.",
    draftKept: "Nacrt ostaje dok je ovaj tab otvoren, i kad odeš na drugu stranicu. Zatvaranje ili osvežavanje taba ga briše.",
    // Povratnica, CC i odgovori (PLAN.md 4.21).
    envelope: "SASE · povratnica",
    envelopeAsk: "Prikupi kovertirane odgovore",
    envelopeIntro: "Pošalji pitanje i prikupi stavove, procene ili predviđanja. Svaki odgovor ostaje privatan i zapečaćen do svog vremena otvaranja. Odgovori stižu samo tebi; ne objavljuju se automatski.",
    envelopeDetails: "Kako funkcioniše povratnica",
    envelopeNote: "Svaki primalac može da ti pošalje jedan privatan odgovor, zapečaćen tvojim ključem, i kad te ne prati. Kapsulu i odgovor plaćaš sada (2 marke po primaocu); drugi odgovor iste osobe košta nju jednu marku. Sve ostaje na tvojoj strani Prepiska, odvojeno od ostalog.",
    envelopeFixed: "Odgovori se otvaraju u vreme koje odredim",
    envelopeFree: "Odgovori se otvaraju kad primalac izabere",
    envelopeOpens: (time: string) => `Odgovori se otvaraju ${time}.`,
    envelopeDeadline: "Posle tog vremena odgovor se ne prima.",
    envelopeCost: (n: number) => (n === 1 ? "Sa povratnicom se troši 1 marka." : `Sa povratnicom se troši ${n} ${n < 5 ? "marke" : "maraka"}.`),
    cc: "CC",
    ccNote: (max: number) => `Još do ${max} članova koje pratiš dobija istu kapsulu, a svi sa spiska vide ko je još primio (imena su zapečaćena unutra; naš server spisak ne vidi). Svako dobija svoju kapsulu.`,
    replyTitle: "Odgovor",
    replyNote: "Privatan odgovor na kapsulu sa povratnicom.",
    replyTo: (name: string) => `Za ${name}, ko traži ovaj odgovor:`,
    replyOnlyToSender: "Odgovor ide samo toj osobi, zapečaćen njenim ključem. Niko iz CC ga ne vidi.",
    replyTimeFixed: (time: string) => `Pošiljalac je odredio vreme otvaranja: ${time}. Tvoj odgovor se otvara tada.`,
    replyTimeFree: "Pošiljalac je vreme otvaranja ostavio tebi.",
    replyExpired: "Vreme koje je pošiljalac odredio je prošlo; odgovor se više ne prima.",
    replyFirstCost: () => "Ovaj odgovor plaća marka pošiljaoca. Tvoja kopija, ako je zadržiš, je besplatna.",
    replyLaterCost: () => "Već je poslat jedan odgovor; ovaj troši jednu marku. Raniji odgovor ostaje i pošiljalac vidi oba.",
    replyUnavailable: "Član koji traži odgovor više nema ključ, pa odgovor ne može da se zapečati.",
    ccLeftOut: "Spisak zapečaćen u kapsuli i dalje ih navodi u CC, jer je potpisan pre slanja: reci ostalima, ako je važno.",
    newcomer: "Mejlom",
    discreet: "Diskretno",
    discreetNote: "U listama primaoca piše „Diskretan pošiljalac“ umesto tvog imena dok ne klikne kapsulu. Samo primalac može da vidi da si to ti; tekst i dalje čeka svoje vreme.",
    blockedOne: (name: string) => `${name} ne prima tvoje privatne kapsule.`,
    blockedMany: (names: string) => `Ovi članovi ne primaju tvoje privatne kapsule i neće dobiti ovu: ${names}.`,
    groupAllBlocked: "Niko u ovoj grupi ne prima tvoje privatne kapsule.",
    leftOut: (names: string) => `Poslato grupi, osim: ${names}. Oni ne primaju tvoje privatne kapsule.`,
    toSent: "Idi na Poslato",
    newcomerNote: "Šaljemo pozivnicu mejlom. Kad se pridruži i zatraži kapsulu, ti potvrđuješ da je to ta osoba i kapsula se zapečati njenim ključem; do tada postoji samo tvoja kopija.",
    severalMembers: (n: number) => `Grupa${n ? ` (${n})` : ""}`,
    severalNote: (max: number) => `Od 2 do ${max} članova koje pratiš. Svako dobija svoju kapsulu; niko ne vidi kome je još poslata.`,
    loadGroups: "Dodaj iz mojih grupa (traži PIN)",
    manyKeysChanged: (names: string) => `Ovi ključevi su se promenili otkad ih je ovaj browser video: ${names}. Pre slanja proveri otiske sa tim osobama.`,
    manyKeysChecked: "Otisci novih ključeva su provereni",
    groupCost: (n: number) => (n === 1 ? "Troši 1 marku, po jednu za svakog primaoca. Tvoja kopija je besplatna." : `Troši ${n} ${n < 5 ? "marke" : "maraka"}, po jednu za svakog primaoca. Tvoja kopija je besplatna.`),
    budget: (limit: number, total: boolean) => (total ? `${limit} kapsula ukupno` : `${limit} kapsula dnevno`),
    subject: "Naslov",
    subjectNote: "Obavezan. Naslov se nikad ne šifruje: ko vidi kapsulu, odmah ga čita, i pre otključavanja, a vidi ga i naš server.",
    intro: "Kratki opis (nije obavezan)",
    introHint: "Čita se pre otvaranja: kod javne kapsule svako, kod privatne samo primalac. U listi stoji odmah posle naslova.",
    visibleBefore: "Vidljivo pre otvaranja",
    sealedTitle: "Sadržaj kapsule",
    sealedNote: "Tekst i fajlovi se šifruju u tvom pregledaču. Niko, ni Vrelis, ne može da ih pročita pre vremena otvaranja.",
    sealedUntil: (time: string) => `Zaključano do ${time}`,
    addFiles: "Dodaj fajlove",
    introCount: (used: string, max: string) => `${used} / ${max} znakova`,
    presets: { m5: "5 minuta", h1: "1 sat", d1: "1 dan", w1: "1 nedelja", y1: "1 godina" },
    needKey: "Za pisanje kapsula postavi ključ na ovom uređaju.",
    noKeyYet: "Za pisanje kapsula treba ti ključ.",
    title: "Napiši kapsulu",
    message: "Tekst",
    bytes: (used: string, max: string) => `${used} / ${max} bajtova`,
    files: "Fajlovi",
    madeSmaller: " · smanjeno, metapodaci uklonjeni",
    preparing: "Pripremam… ",
    filesNote: (used: string, max: string, count: number) =>
      `${used} od ${max}, najviše ${count} fajlova. Fotografije se smanjuju i gube metapodatke, uključujući lokaciju. Fajlovi se šifruju ovde; njihovi ključevi putuju unutar vremenske brave.`,
    tooManyFiles: (n: number) => `Najviše ${n} fajlova po kapsuli.`,
    opensIn: "Otvara se za",
    pickDate: "Izaberi datum",
    opensAt: (time: string, round: string) => (
      <>
        Otvara se <b>{time}</b> (drand runda {round}, ±3 s). Do tada niko ne može da je pročita, ni ti.
      </>
    ),
    tooLate: "Izaberi vreme pre 1. januara 2126.",
    inPast: "Izaberi vreme u budućnosti.",
    whoCanRead: "Ko može da je pročita",
    everyone: "Svi",
    oneMember: (name: string | null) => name ?? "Jedna osoba",
    search: "Traži članove",
    followFirst: "Privatna kapsula ide samo članovima koje pratiš. Prvo nekoga zaprati (Nalog, ili „Prati” na njegovoj kapsuli).",
    youMark: " (ti)",
    keyOf: (name: string) => `Ključ: ${name}: `,
    since: (time: string) => ` · od ${time}`,
    fullKey: (name: string) => `Ceo ključ, za poređenje sa: ${name}`,
    keyChanged: (name: string, when: string | null, was: string, now: string) => (
      <>
        Ključ člana {name} se promenio{when ? ` ${when}` : ""} (bio je <code>{was}</code>). Proveri sa tom osobom van aplikacije da je <code>{now}</code> zaista
        njen novi ključ, pre nego što pošalješ bilo šta privatno. Slanje čeka tvoju potvrdu.{" "}
      </>
    ),
    checked: (name: string) => `Novi ključ je proveren sa: ${name}`,
    toSelf: "Kapsula samom sebi je zapečaćena tvojim ključem, pa je otvaraš posle vremena kao svaki primalac. Dodatna kopija nije potrebna.",
    keepCopy: "Sačuvaj besplatnu kopiju za mene",
    copyOffConfirm: "Poslati bez kopije? Tada ovu kapsulu nećeš moći da pročitaš, čak ni kad se otvori. Može samo primalac.",
    uses: (n: number) => (n === 1 ? "Troši 1 marku." : `Troši ${n} ${n < 5 ? "marke" : "maraka"}.`),
    usesBudget: (n: number, budget: string) => `Računa se kao ${n} od tvojih ${budget}.`,
    stampStatus: (cost: number, left: number) => `${cost === 1 ? "1 marka" : `${cost} ${cost < 5 ? "marke" : "maraka"}`} za ovu kapsulu · imaš ${left}.`,
    stampRule: "Jedna marka je jedna isporuka jednoj osobi. Tvoja kopija je besplatna.",
    stampShort: (cost: number, left: number) => `Treba ${cost} ${cost === 1 ? "marka" : cost < 5 ? "marke" : "maraka"}, a imaš ${left}.`,
    seeBilling: "Planovi i marke",
    moreSpace: "Treba ti mesta za veće fajlove? Pogledaj planove.",
    copyOn: (name: string) => `Besplatna kopija se pečati tvojim ključem, pa možeš da pročitaš ovu kapsulu u Poslato kad se otvori, kao i ${name}.`,
    copyOff: (name: string) => `Bez kopije samo ${name} može da otvori ovu kapsulu. Ni ti nećeš moći da je pročitaš, čak ni kad se otvori. U Poslato se vidi samo kad se otvara.`,
    sealing: "Pečatim…",
    sendAgain: "Pošalji ponovo",
    seal: "Zapečati i pošalji",
    notDelivered: (detail: string) => `Ništa nije isporučeno: ${detail}`,
    unconfirmed: "Nismo mogli da potvrdimo isporuku (problem sa vezom). „Pošalji ponovo” šalje istu kapsulu; nikad se ne čuva dvaput.",
  },

  capsules: {
    toMany: (names: string) => `za: ${names}`,
    unlockedTab: "Otključane",
    lockedTab: "Još zaključane",
    titles: {
      feed: ["Javno", "Javne kapsule svih članova pilota."],
      inbox: ["Primljeno", "Privatne kapsule upućene tebi."],
      sent: ["Poslato", "Tvoje kapsule."],
      watching: ["Čekam", "Javne kapsule čije otvaranje čekaš. Gore je ona koja se sledeća otvara."],
      envelopes_sent: ["Prepiska, tražim ja", "Kapsule sa povratnicom koje šalješ."],
      envelopes_received: ["Prepiska, traže od mene", "Kapsule sa povratnicom upućene tebi."],
    },
    everyone: "Svi",
    following: (n: number) => `Koje pratim (${n})`,
    followNobody: "Još nikog ne pratiš. Upotrebi „Prati” na kapsuli da ovde vidiš kapsule tog autora.",
    empty: "Ovde još nema ničega.",
    emptyFiltered: "Nijedna kapsula ne odgovara ovim filterima.",
    emptyWrite: "Napiši kapsulu",
    nextOpens: (time: string) => `Sledeća kapsula se otvara ${time}.`,
    newGroup: "Nove",
    openedGroup: "Već otvorene",
    newLabel: "Nova",
    emptyStates: {
      feed: {
        unlocked: ["Još nema otvorenih javnih kapsula.", "Javne kapsule svih u pilotu pojavljuju se ovde kada im prođe vreme otvaranja. Od tada svako u pilotu može da ih pročita.", true],
        locked: ["Nijedna javna kapsula ne čeka.", "Zapečaćene javne kapsule čekaju ovde sa datumom otvaranja i odbrojavanjem. Predmet, i uvod ako ga je autor napisao, čitaš odmah; tekst tek u trenutku otvaranja.", true],
        watching: ["Ne pratiš nijednu kapsulu.", "Izaberi \"Čekaj otvaranje\" na zapečaćenoj javnoj kapsuli i ona ostaje ovde dok se ne otvori.", false],
        following: ["Ništa od ljudi koje pratiš.", "Ovde se pojavljuju javne kapsule ljudi koje pratiš. Zaprati nekoga preko imena na kapsuli ili preko pretrage.", false],
        archived: ["Ništa nije arhivirano.", "Kapsule koje arhiviraš nestaju iz listi Javno, Primljeno i Poslato i čekaju ovde. Arhiviranje skriva kapsulu samo tebi; vrati je kad hoćeš.", false],
      },
      inbox: {
        unlocked: ["Još nema otvorenih privatnih kapsula.", "Privatne kapsule poslate tebi pojavljuju se ovde kada im prođe vreme otvaranja. Otvara ih samo tvoj ključ.", false],
        locked: ["Nijedna privatna kapsula te ne čeka.", "Privatne kapsule upućene tebi čekaju ovde, zapečaćene, do vremena otvaranja. Član koji te prati može da ti pošalje kapsulu.", false],
        archived: ["Ništa nije arhivirano.", "Kapsule koje arhiviraš nestaju iz listi Javno, Primljeno i Poslato i čekaju ovde. Arhiviranje skriva kapsulu samo tebi; vrati je kad hoćeš.", false],
        blocked: ["Ništa od blokiranih članova.", "Privatne kapsule članova koje blokiraš čuvaju se ovde, odvojeno od liste Primljeno.", false],
      },
      sent: {
        unlocked: ["Nijedna tvoja kapsula se još nije otvorila.", "Kapsule koje pošalješ pojavljuju se ovde kada im prođe vreme otvaranja. Privatnu možeš da pročitaš samo ako je sačuvana tvoja kopija.", true],
        locked: ["Nemaš zapečaćenih kapsula.", "Kapsule koje pošalješ čekaju ovde sa datumom otvaranja dok se ne otvore: tebi, članu, ili adresi nekoga ko još nije član.", true],
        archived: ["Ništa nije arhivirano.", "Kapsule koje arhiviraš nestaju iz listi Javno, Primljeno i Poslato i čekaju ovde. Arhiviranje skriva kapsulu samo tebi; vrati je kad hoćeš.", false],
      },
    },
    selectAll: "Izaberi sve prikazane",
    downloadSelected: (n: number) => `Preuzmi izabrano (${n})`,
    includeOpened: "Uključi otvoren tekst i fajlove (na tvom disku nisu šifrovani)",
    preparing: (n: number) => `Pripremam ${capsules(n)}…`,
    saved: (n: number, skipped: number) =>
      `Sačuvano: ${capsules(n)}${skipped ? `; preskočeno: ${skipped} (samo primalac može da ih pročita)` : ""}.`,
    loadOlder: "Učitaj starije",
    damaged: (reason: string) => `Oštećena kapsula: ${reason}`,
    fromUnknown: "od nepoznatog",
    fromYou: "od tebe",
    from: (name: string) => `od: ${name}`,
    now: (name: string) => ` · sada ${name}`,
    noName: " · bez imena u kapsuli",
    toEveryone: "svima",
    to: (name: string) => `za: ${name}`,
    privateText: "Privatno. Otvori je PIN-om da vidiš ko je poslao i kada se otvara; tekst kad dođe to vreme.",
    opensIn: (d: string) => `Otvara se za ${d}`,
    private: "privatno",
    discreetSender: "Diskretan pošiljalac",
    unverifiedSender: "Nepotvrđen pošiljalac",
    unverifiedNote: "Ključ koji je potpisao ovu kapsulu nije ključ naloga koji ju je poslao. Ne veruj imenu koje nosi.",
    intro: "Kratki opis",
    selectForDownload: "Izaberi za preuzimanje",
    written: (time: string) => `napisano ${time}`,
    opensAt: (time: string) => ` · otvara se ${time}`,
    opensWhenOpened: " · vreme otvaranja vidiš kad je otvoriš",
    opensRecipientOnly: " · vreme otvaranja zna samo primalac",
    downloadZip: "Kapsula sa fajlovima kao .zip, sa offline otvaračem",
    downloadJson: "Kapsula kao .json fajl",
    watch: "Čekaj otvaranje",
    stopWatching: "Ne čekaj više",
    follow: (name: string) => `Prati: ${name}`,
    unfollow: (name: string) => `Ne prati: ${name}`,
    keyMissing: "Tvoj ključ nije na ovom uređaju. Postavi ga da vidiš ovu kapsulu.",
    oldKeyMissing: "Poslato na tvoj raniji ključ koji nije u privesku. Dodaj njegovu frazu pod Nalog, Ključevi.",
    suspect: "opozvan ključ",
    suspectNote: "Ključ kojim je ovo potpisano kasnije je opozvan kao ukraden. Možda nije od pošiljaoca.",
    noCopy: "Samo primalac može da je otvori. Kopija nije sačuvana.",
  },

  roles: {
    owner: "Vlasnik",
    ownerHint: "Vodi Vrelis i jedini postavlja i uklanja admine.",
    admin: "Admin",
    adminHint: "Pomaže u vođenju Vrelisa: pozivnice, članovi, registracija.",
    service: "API nalog",
    serviceHint: "Servis koji čita i šalje preko Vrelis API-ja, tokenima.",
  },

  account: {
    status: {
      none: "Tvoj ključ nije na ovom uređaju.",
      mismatch: "Ovaj uređaj ima drugi identitet od trenutnog identiteta tvog naloga.",
      locked: "Na ovom uređaju, zaključan pristupnim ključem + PIN-om; PIN se traži kad otvaraš ili šalješ nešto privatno.",
      unlocked: "Na ovom uređaju, otključan.",
    },
    manageKeys: "Upravljanje ključevima: podaci, reset, privezak",
    yourKey: "Tvoj ključ",
    preferences: "Podešavanja",
    privacy: "Privatnost",
    hideSenders: "Prikaži pošiljaoce u listama tek kad kliknem kapsulu",
    hideSendersNote: "Zbog pogleda preko ramena: svaka privatna kapsula pokazuje „Diskretan pošiljalac“ dok je ne klikneš, na svim uređajima.",
    notifications: {
      title: "Obaveštenja na mejl",
      note: "Mejlovi stižu na adresu tvog naloga. Svaki ima link kojim isključuješ tu vrstu.",
      kinds: {
        new_private: "Stigne mi nova privatna kapsula",
        new_public: "Jednom dnevno: nove javne kapsule ljudi koje pratim",
        upcoming: "Kapsule se uskoro otvaraju: privatne za mene, javne ljudi koje pratim i one čije otvaranje čekam",
        reply_due: "Ističe vreme za odgovor na povratnicu",
        unlocked: "Privatna kapsula za mene može da se otvori",
      },
      lead: "Koliko unapred",
      attach: "Priloži kapsulu kao fajl za offline otvarač",
      attachNote: "Fajl ostaje zaključan: otvara se frazom za oporavak kad dođe njegovo vreme. Fajlovi kapsule ne idu u prilog.",
      utc: "Vremena u mejlovima su po UTC-u.",
    },
    preferencesNote: "Važi samo u ovom browser-u.",
    identity: "Identitet: ",
    plain: " Čuva se bez zaštite pristupnim ključem u ovom browser-u.",
    noServerCopy: "Nema kopije na našem serveru.",
    serverBlocked: "Kopija na našem serveru je blokirana posle 10 pogrešnih PIN-ova.",
    serverCopy: (left: number) => `Kopija na našem serveru: zaključana pristupnim ključem + PIN-om, ostalo je ${left} ${pl(left, "pokušaj", "pokušaja", "pokušaja")}.`,
    setUpAgain: "Ponovo postavi ovaj uređaj frazom za oporavak",
    setUpAgainNote: " (novi PIN i pristupni ključ; zamenjuje kopiju na serveru).",
    notSetUp: "Još nije postavljen.",
    signIns: {
      title: "Kako se prijavljuješ",
      note: "Svaki od ovih načina otvara tvoj nalog. Nijedan ne otvara tvoj ključ: za to i dalje trebaju pristupni ključ i PIN, ili fraza za oporavak.",
      kind: { google: "Google", password: "Lozinka", passkey: "Pristupni ključ" },
      since: (day: string) => `(od ${day})`,
      remove: "ukloni",
      confirmRemove: (kind: string) => `Ukloniti ovaj način prijave (${kind})?`,
      noPasskey: "Još nijedan pristupni ključ ne služi za prijavu. Pristupni ključ koji napraviš kad podešavaš ključ na uređaju služi i za prijavu.",
      addPassword: (email: string) => `Dodaj lozinku za ${email}. Šaljemo link na tu adresu; lozinka radi kad ga otvoriš.`,
      addPasswordButton: "Dodaj lozinku",
    },
    following: "Ljudi",
    peopleLink: "Pratim, prate me, blokirani i sakriveni",
    peopleNote: " su na stranici Ljudi.",
    followNobody: "Još nikog. Upotrebi „Prati” na javnoj kapsuli; one koje pozoveš i onoga ko je tebe pozvao pratiš automatski. Tvoje privatne kapsule mogu da dobiju samo oni koje pratiš.",
    unfollow: "ne prati",
    followSource: { invited: "tvoja pozivnica", inviter: "pozivnica od te osobe" },
    contactsLink: "Adresar: grupe i nadimci",
    contactsNote: " (šifrovan tvojim ključem; mi ne možemo da ga pročitamo).",
    keepTitle: "Čuvaj svoje kapsule i bez nas",
    opener: "Preuzmi offline otvarač",
    openerNote: ": jedan HTML fajl koji na tvom računaru otvara preuzete kapsule, privatne uz frazu za oporavak. Nikad ne kontaktira vrelis.com.",
    deviceTitle: "Ovaj uređaj i tvoje sesije",
    actions: (
      <>
        <li>
          <b>Zaključaj</b>: odmah uklanja ključ iz memorije; PIN se ponovo traži.
        </li>
        <li>
          <b>Odjavi se</b>: zaključava i završava ovu sesiju. Ključ i otvorene kapsule ostaju na ovom uređaju.
        </li>
        <li>
          <b>Odjavi se svuda</b>: završava tvoje sesije na svim uređajima.
        </li>
        <li>
          <b>Zaboravi ovaj uređaj</b>: odjavljuje te i briše tvoj ključ i sve što je Vrelis sačuvao za tebe u ovom browser-u.
        </li>
      </>
    ),
    lock: "Zaključaj",
    logout: "Odjavi se",
    logoutEverywhere: "Odjavi se svuda",
    forget: "Zaboravi ovaj uređaj",
    confirmEverywhere: "Da te odjavimo na svim uređajima i browser-ima, i na ovom?",
    confirmForget:
      "Da zaboravimo ovaj uređaj?\n\nOvaj browser briše tvoj ključ, tekst otvorenih kapsula, beleške o poslatim kapsulama i proverene ključeve, pa te odjavljuje. Kapsule na serveru ostaju.\n\nZa ponovno korišćenje identiteta ovde treba ti pristupni ključ i PIN, ili fraza za oporavak od 12 reči. Imaš li je?",
    profileTitle: "Tvoj profil",
    profileNote: (fp: string) => (
      <>
        Svaka kapsula koju pošalješ nosi ova polja, potpisana, onakva kakva su u tom trenutku. Nikad tvoj email ni tvoj Google nalog. Primaoci ih vide uz otisak
        tvog ključa <code>{fp}</code>.
      </>
    ),
    displayName: "Ime za prikaz",
    nickname: "Nadimak (nije obavezan)",
    about: (n: number) => `O meni (nije obavezno, do ${n} znakova)`,
    saveProfile: "Sačuvaj profil",
    saved: "Sačuvano. Nove kapsule nose ovo ime; već poslate zadržavaju svoje.",
  },

  guide: {
    back: "Nazad na Vrelis",
    contents: "Sadržaj",
  },

  invites: {
    title: "Pozovi nekoga",
    text: "Šaljemo pozivnicu mejlom na ovu adresu. Kad se pridruži, pratite jedno drugo.",
    limit: (n: number) => `Najviše ${n} otvorenih pozivnica.`,
    send: "Pošalji pozivnicu",
    mailed: (email: string) => `Pozivnica je poslata na ${email}.`,
    resent: (email: string) => `${email} već ima tvoju pozivnicu; poslata je ponovo, sa novim rokom važenja.`,
    notMailed: (why: string) => `Mejl nije poslat: ${why}`,
    orCopy: "Link možeš i ručno da proslediš:",
    open: "čeka",
    used: "prihvaćena",
  },

  envelopes: {
    inboxNote: "Zahtevi za odgovor i odgovori na tvoje zahteve čuvaju se u Prepisci.",
    inboxUnopened: (n: string) => `Neotvoreni zahtevi za odgovor: ${n}. Čuvaju se u Prepisci.`,
    inboxLink: "Otvori Prepisku",
    title: "Prepiska",
    subtitle: "Kapsule sa povratnicom i odgovori na njih, odvojeno od ostalog.",
    tabs: { sent: "Tražim ja", received: "Traže od mene", archived: "Arhivirano" },
    empty: "Još nema prepiske.",
    emptyNote: "Napiši privatnu kapsulu i štikliraj \"Traži odgovor\": ona i odgovori na nju stižu ovde.",
    emptyNotes: {
      sent: "Zahtev poslat sa \"Traži odgovor\" čeka ovde sa svim odgovorima na njega. Svaki primalac odgovara privatno, a odgovori ostaju zapečaćeni do izabranog vremena, i za tebe.",
      received: "Kada te neko zamoli za odgovor, zahtev stiže ovde. Tvoj odgovor ide samo toj osobi, zapečaćen do njenog vremena; prvi je već platila osoba koja pita.",
      archived: "Zahtevi koje arhiviraš čekaju ovde. Arhiviranje ih skriva samo tebi; vrati ih kad hoćeš.",
    },
    asksReply: "traži odgovor",
    asksReplyNote: "Pošiljalac traži privatan odgovor. Odgovor ide samo pošiljaocu.",
    replyOpensAt: (time: string) => `odgovor se otvara ${time}`,
    replyOpensFree: "odgovor se otvara kad primalac izabere",
    termsSent: (when: string, paid: number) => `Tražiš odgovor; ${when}. Plaćeno: ${paid}.`,
    termsReceived: (when: string) => `Pošiljalac traži odgovor; ${when}. Tvoj odgovor ide samo pošiljaocu.`,
    expired: "Vreme je prošlo; odgovor se više ne prima.",
    replied: (n: number, total: number) => `odgovorilo ${n} od ${total}`,
    noReply: "još nema odgovora",
    replies: (n: number) => `${n} ${pl(n, "odgovor", "odgovora", "odgovora")}`,
    replyIndex: (n: number) => `odgovor ${n}`,
    stamp: "marka",
    stampNote: "Ko je platio ovaj odgovor: marka pošiljaoca (prvi odgovor) ili onaj ko je odgovorio.",
    paidBy: (name: string) => `platio ${name}`,
    youPaid: "iz tvog budžeta",
    matchesTime: "otvara se u vreme koje si odredio",
    mismatchTime: "ne poklapa se sa povratnicom",
    reply: "Odgovori",
    replyAgain: "Odgovori ponovo",
    onlyToSender: "Tvoj odgovor ide samo pošiljaocu.",
    myReplies: "Moji odgovori",
    moreReplies: "Više od 100 odgovora; ostali nisu na spisku.",
    senderUnknown: "Pošiljalac ove kapsule još nije poznat. Prvo je otvori.",
    toLabel: "Za:",
    ccLabel: "CC:",
  },

  billing: {
    title: "Obračun",
    pageNote: "Tvoj plan, tvoje marke i tvoj prostor.",
    previewNote: "Plaćanje ide preko našeg platnog provajdera, unutar ove strane. Već poslato ostaje čitljivo šta god se desilo sa planom.",
    seePlans: "Planovi i marke",
    planNames: { free: "Free", legacy: "Legacy", start: "Start" } as Record<string, string>,
    planTitle: "Tvoj plan",
    planLine: (name: string, monthly: number) => `${name} · ${monthly} maraka mesečno`,
    attachmentLine: (size: string) => `Do ${size} fajlova u jednoj kapsuli`,
    activatedUntil: (date: string) => `Start je uključen do ${date}`,
    noAutoRenew: "Ne obnavlja se sam.",
    renewsOn: (date: string) => `Obnavlja se automatski ${date}.`,
    endsOn: (date: string) => `Otkazano: ističe ${date} i ne obnavlja se.`,
    renewalUnknown: "Obnavljanje: pogledaj Računi i pretplata.",
    refreshOn: (date: string) => `Mesečne marke se obnavljaju ${date} (UTC).`,
    rightsTitle: "Od čega se plan sastoji",
    rightsNote: "Može važiti više prava odjednom: dobijaš najveći broj maraka i najviše prostora, a dodatni prostor se sabira.",
    rightSource: { launch: "Član od početka", manual: "Dao admin", subscription: "Start", pack_storage: "Prostor iz paketa", pack_attachment: "Fajlovi iz paketa" } as Record<string, string>,
    rightLine: (what: string, from: string, until: string | null) => (until ? `${what} · ${from} do ${until}` : `${what} · od ${from}, bez datuma isteka`),
    rightStamps: (n: number) => `${n} maraka mesečno`,
    rightStorage: (size: string) => `${size} prostora`,
    rightExtraStorage: (size: string) => `+${size} prostora`,
    rightFiles: (size: string) => `${size} fajlova po kapsuli`,
    stampsTitle: "Marke",
    stampsLeft: (n: number) => (n === 1 ? "Ostala je 1 marka" : `Ostalo je ${n} maraka`),
    stampExplain: "Jedna marka plaća jednu isporuku jednom primaocu. Tvoja kopija je besplatna. Kapsule koje su već poslate ostaju čitljive šta god se desilo sa planom ili markama.",
    upgrade: "Nadogradi",
    buyPack: "Kupi",
    checkoutTitle: "Plaćanje",
    checkoutClose: "Zatvori",
    checkoutTest: "Probni režim: koristi probne kartice posrednika. Pravi novac se ne prenosi.",
    checkoutFake: "Probni posrednik nema formu za plaćanje. Testovi sami završavaju ovu kupovinu.",
    checkoutConfirming: "Plaćeno. Čekamo da posrednik potvrdi uplatu; plan ili marke će se pojaviti ovde za trenutak.",
    checkoutActivating: "Plaćeno. Posrednik je potvrdio uplatu; plan ili marke se upravo aktiviraju.",
    checkoutSlow: "Potvrda traje duže nego obično. Možeš da napustiš stranicu: kupovina je sačuvana, a plan ili marke se pojavljuju kad posrednik potvrdi.",
    checkoutError: "Forma za plaćanje nije mogla da se otvori ili je prijavila grešku. Vrelis ništa nije naplatio; pokušaj ponovo.",
    checkoutNotReady: "plaćanje još nije potpuno podešeno",
    checkoutDone: "Hvala. Kupovina je aktivna.",
    portal: "Računi i pretplata",
    portalNote: "Otvara stranu posrednika za plaćanje u novom tabu: računi, način plaćanja, promena ili otkazivanje pretplate.",
    monthMeter: (used: number, granted: number) => `Ovaj mesec: potrošeno ${used} od ${granted}`,
    monthMeterName: "Marke potrošene ovog meseca",
    held: (n: number) => `${n} od ovih maraka je zadržano za kapsule koje čekaju nove članove. Vraćaju se ako se kapsula odbije ili istekne.`,
    expiring: "Marke koje ističu u narednih 30 dana",
    expiringItem: (units: number, date: string) => `${units} do ${date}`,
    expiringMore: "i još kasnije",
    storageTitle: "Prostor",
    storageLine: (used: string, quota: string) => `Iskorišćeno ${used} od ${quota}`,
    storageMeterName: "Iskorišćen prostor",
    storageNote: "U prostor se računaju šifrovani fajlovi koji su otpremljeni. Fajlovi u već poslatim kapsulama ostaju dostupni tebi i primaocima, bez obzira na plan.",
    overQuota: "Prostor koji plan daje je prekoračen. Sve što je poslato ostaje kako jeste i tekst i dalje možeš da šalješ, ali novi fajlovi se ne mogu dodati dok se prostor ne oslobodi ili ne dobije više.",
    chooseTitle: "Izaberi plan ili marke",
    carouselPrev: "Prethodni planovi",
    carouselNext: "Još planova",
    perMonth: "mesec",
    perYear: "godina",
    every: (unit: "month" | "year", n: number) => {
      if (n <= 1) return `/ ${unit === "year" ? "godina" : "mesec"}`;
      const few = n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14);
      return unit === "year" ? `${few ? "svake" : "svakih"} ${n} ${few ? "godine" : "godina"}` : `${few ? "svaka" : "svakih"} ${n} ${few ? "meseca" : "meseci"}`;
    },
    planDetail: (monthly: number, storage: string, files: string) => `${monthly} maraka mesečno, ${storage} prostora, ${files} fajlova po kapsuli.`,
    free: { name: "Free", text: "Važi kada ništa drugo ne važi." },
    legacy: { name: "Legacy", text: "Dobili su ga prvi članovi na početku." },
    startNotNeeded: "Start ti ne bi dodao ništa na ono što već imaš, pa se ne nudi.",
    current: "Tvoj plan",
    historyTitle: "Istorija",
    historyNote: "Potrošene marke, dodate marke i tvoje kupovine, najnovije prvo.",
    tabs: { uses: "Potrošene marke", grants: "Dodate marke", orders: "Kupovine" } as Record<string, string>,
    nothingYet: "Još ništa. Ovde se redom upisuju potrošene marke, dobijene marke i kupovine.",
    packDetail: (stamps: number, stampMonths: number, storage: string, files: string, fileMonths: number) => [stamps > 0 ? `${stamps} maraka na ${stampMonths} meseci` : null, storage !== "0 KiB" ? `još ${storage} prostora zauvek` : null, files !== "0 KiB" ? `${files} fajlova po kapsuli ${fileMonths} meseci` : null].filter(Boolean).join(", ") + ".",
    nothingOnSale: "Za sada se ništa ne prodaje.",
    notSelling: "Prodaja još nije uključena. Planovi ispod pokazuju kako će raditi; ništa ne može da se kupi i ništa se ne naplaćuje.",
    useSend: (units: number, messages: number) => `Poslato: ${messages} ${messages === 1 ? "kapsula" : "kapsule"} · ${units} ${units === 1 ? "marka" : "marke"}`,
    useSendFree: (messages: number) => `Poslato: ${messages} ${messages === 1 ? "kapsula" : "kapsule"} · bez maraka`,
    useReserve: (units: number) => `Zadržano ${units} ${units === 1 ? "marka" : "marke"} za kapsulu koja čeka novog člana`,
    useRefund: (units: number) => `Vraćeno ${units} ${units === 1 ? "marka" : "marke"} iz zatvorene kapsule na čekanju`,
    pendingStatus: { waiting: "čeka", requested: "zatražena", delivered: "isporučena", declined: "odbijena", expired: "istekla" } as Record<string, string>,
    grantReasons: { period: "Mesečne marke", pack: "Paket maraka", manual: "Dodao admin", refund: "Vraćene marke" } as Record<string, string>,
    grantLine: (left: number, units: number, until: string) => `Ostalo ${left} od ${units} · do ${until}`,
    orderStatus: { open: "Započeto, nije plaćeno", completed: "Plaćeno", expired: "Nije plaćeno na vreme", unknown: "Posrednik nije potvrdio" } as Record<string, string>,
    accountCard: "Plan i marke",
    accountLine: (plan: string, stamps: number) => `${plan} · ostalo ${stamps} maraka`,
    accountStorage: (used: string, quota: string) => `Prostor ${used} od ${quota}`,
    manage: "Upravljaj u Obračunu",
    badge: (n: number) => (n === 1 ? "1 marka" : `${n} maraka`),
    badgeLabel: (n: number) => `Preostale marke: ${n}. Otvori Obračun.`,
    badgeNote: (messages: number, limit: number) => `Otvori Obračun. Tehničko ograničenje: ${messages} od ${limit} isporuka u poslednja 24 sata.`,
    serviceNote: "Servisni nalozi nemaju plan ni marke. Njihovo tehničko ograničenje je u zaglavlju.",
    pricing: {
      title: "Planovi i marke",
      lead: "Planovi i paketi maraka, kako ih prodaje naš platni provajder.",
      stampDef: "Jedna marka je jedna isporuka jednom primaocu. Tvoja kopija je besplatna. Poslata kapsula ostaje čitljiva tebi i primaocima šta god se desilo sa planom.",
      signIn: "Prijavi se da vidiš svoj plan",
      open: "Otvori Obračun",
      pilotLead: "Vrelis je besplatan tokom pilota. Ništa se ne naplaćuje: ne unosiš karticu i ništa se ne obnavlja samo.",
      pilotTitle: "Šta dobijaš u pilotu",
      pilotPlans: "Svaki član ima plan: broj maraka svakog meseca i prostor za fajlove. Kad se prijaviš, Obračun pokazuje tvoj plan i koliko ti je ostalo.",
      pilotChanges: "Ako kasnije uvedemo plaćene planove, ponudu i njene uslove videćeš pre nego što odlučiš. Korišćenje pilota te ne obavezuje ni na kakvo plaćanje, a poslate kapsule ostaju čitljive šta god se desilo sa planom.",
    },
  },
  adminBilling: {
    reasonNeeded: "Navedi razlog od 3 do 200 znakova.",
    launchTitle: "Plan za početak (Legacy)",
    launchLine: (count: number, cap: number) => `Legacy je dobilo ${count} od ${cap} članova.`,
    launchOn: "Novi članovi sada dobijaju Legacy.",
    launchOffSwitch: "Prekidač je isključen: novi članovi dobijaju Free.",
    launchFull: "Sva mesta su iskorišćena: novi članovi dobijaju Free.",
    launchClose: "Prestani da daješ Legacy",
    launchOpen: "Daj Legacy novim članovima",
    confirmLaunchClose: "Prestati sa davanjem Legacy-ja novim članovima? Članovi koji ga imaju zadržavaju ga.",
    confirmLaunchOpen: "Ponovo davati Legacy novim članovima, dok ima mesta?",
    launchNote: "Otvaranje registracije samo po sebi ne daje Legacy. Iskorišćeno mesto se nikad ne oslobađa.",
    memberTitle: "Plan i marke",
    memberShow: "Prikaži plan i marke",
    memberLoading: "Učitavanje…",
    memberLine: (plan: string, stamps: number, used: string, quota: string) => `${plan} · ${stamps} maraka · prostor ${used} od ${quota}`,
    rights: "Prava sada i kasnije",
    noRights: "Nema prava osim Free. Prava iz kupovine ili od administratora pojavljuju se ovde sa datumima.",
    why: (reason: string) => `razlog: ${reason}`,
    endRight: "Završi sada",
    confirmEnd: "Završiti ovo pravo sada? Marke već date za ovaj mesec ostaju.",
    addStamps: "Dodaj marke",
    stamps: "Marke (1 do 10 000)",
    reason: "Razlog (3 do 200 znakova)",
    add: "Dodaj",
    giveRight: "Daj plan",
    plan: "Plan",
    days: "Dana (prazno: bez isteka)",
    give: "Daj",
    addStorage: "Dodaj prostor",
    gib: "GiB (1 do 100)",
    done: "Gotovo.",
    notYours: "Obračun admina ili vlasnika menja samo vlasnik.",
  },

  adminPayments: {
    tab: "Plaćanja",
    providerTitle: "Posrednik za plaćanje",
    providerNames: { fake: "Probni posrednik (bez pravih plaćanja)", paddle: "Paddle", dodo: "Dodo Payments" } as Record<string, string>,
    sandbox: "sandbox: probne kartice, bez pravog novca",
    live: "live: prava plaćanja",
    fakeNote: "Probni posrednik ništa ne prodaje zaista. Njegovi događaji dolaze samo iz lokalnih testova (smoke, E2E).",
    keysSet: "Ključevi su postavljeni.",
    keysMissing: (names: string) => `Nedostaje: ${names}. Postavi ih sa wrangler secret put.`,
    secretNames: { api_key: "API ključ", webhook_secret: "webhook tajna", client_token: "client token (plaćanje na stranici)" } as Record<string, string>,
    webhookAt: "Kod posrednika, webhook šalji na",
    catalogueError: (detail: string) => `Cenovnik posrednika nije mogao da se pročita: ${detail}`,
    refresh: "Pročitaj ponovo",
    pricesTitle: (n: number) => `Cene kod posrednika (${n})`,
    noPrices: "Posrednik još ništa ne prodaje. Napravi proizvode i cene u njegovom panelu, pa pročitaj ponovo.",
    oneOff: "jednokratno",
    notSold: "ne prodaje se na Vrelisu (nije mapirana)",
    notListed: "plaćena, ali je sada nema u spisku posrednika",
    map: "Mapiraj",
    edit: "Promeni",
    grantsPlan: (plan: string) => `daje ${plan} za svaki plaćeni period`,
    inactive: "trenutno se ne prodaje",
    otherMappings: "Mapiranja cena koje posrednik više ne navodi",
    ownerOnly: "Samo vlasnik menja šta cena daje.",
    formLabel: (id: string) => `Šta daje ${id}`,
    kind: "Vrsta",
    kindSubscription: "Pretplata (plan za svaki period)",
    kindPack: "Paket (jednokratno)",
    stamps: "Markice",
    stampMonths: "Markice važe (meseci)",
    storageMib: "Prostor zauvek (MiB)",
    fileMib: "Fajlovi po kapsuli (MiB)",
    fileMonths: "Pravo na fajlove (meseci)",
    active: "Prodaje se na Vrelisu",
    save: "Sačuvaj",
    eventsTitle: (n: number) => `Plaćanja koja nisu dala ništa (${n})`,
    noEvents: "Nema ih. Svaki plaćeni događaj je dao svoja prava.",
    unmapped: "Cena nije mapirana",
    unmatched: "Član nije pronađen",
    flagged: "Plaćeno, potrebna je radnja kod posrednika: vidi napomenu",
    invalid: "Potpisao posrednik, ali nije razumljivo",
    noMember: "bez člana",
    replay: "Primeni ponovo",
    stillWaiting: (detail: string) => `I dalje nema šta da se da: ${detail}`,
    eventsNote: "Prvo mapiraj cenu, pa ponovo primeni događaj. Prava se daju jednom, koliko god puta pokušaš.",
    purchasesTitle: "Kupovine",
    purchaseFilter: { completed: "Plaćene", open: "Započete", all: "Sve" } as Record<string, string>,
    noPurchases: "Nema kupovina. Svaki checkout koji član otvori kod provajdera upisuje se ovde sa stanjem.",
    memberIds: "Kod posrednika",
  },

  pending: {
    incomingTitle: "Čeka te",
    from: (name: string) => `od: ${name}`,
    askText: "Zatraži je od pošiljaoca. Kad potvrdi da si to ti, stiže u Primljeno.",
    needKey: "Prvo podesi svoj ključ; pošiljalac kapsulu pečati njime.",
    ask: "Zatraži",
    asked: "Zatraženo; čeka se potvrda pošiljaoca.",
    declined: "Pošiljalac je odbio.",
    expired: "Isteklo je.",
    outgoingTitle: "Poslato onima koji još nisu članovi",
    to: (email: string) => `za: ${email}`,
    waitingFor: (until: string) => `Čeka da se pridruži i zatraži (do ${until}).`,
    requestedBy: (name: string, email: string, fp: string) => `${name} (${email}, ključ ${fp}) traži ovu kapsulu. Odobri samo ako je to osoba kojoj je kapsula namenjena.`,
    approve: "Odobri",
    decline: "Odbij",
    cancel: "povuci",
    confirmApprove: (name: string, email: string, fp: string) => `Zapečatiti ovu kapsulu za: ${name} (${email}, ključ ${fp})?`,
    confirmDecline: (email: string) => `Povući ovu kapsulu za ${email}? Tvoja kopija ostaje u Poslato.`,
    purpose: "da proslediš kapsulu",
  },

  developersPage: {
    title: "Vrelis API",
    lead: "Servisni nalozi čitaju i šalju vremenske kapsule preko API-ja na /api/v1. Tvoj servis sam pravi, šifruje i potpisuje svaku kapsulu pomoću SDK-a; Vrelis čuva šifrat i proverava potpise, praćenja, blokade i limite. Nikad ne šifruje i ne potpisuje umesto tebe.",
    accessTitle: "Pristup",
    access: [
      "Administrator šalje servisnu pozivnicu na adresu tvog servisa; prijava njome pravi servisni nalog. Ostali članovi ga vide kao običnog člana.",
      "Pod Nalog, Ključevi napravi ključ servisa i čuvaj njegovih 12 reči u tajnim podešavanjima servisa: to je identitet servisa.",
      "Pod Nalog, Za programere napravi API token (prikazuje se jednom) i šalji ga kao Authorization: Bearer <token>.",
    ],
    securityTitle: "Bezbednost",
    security: [
      "Ko ima 12 reči, potpisuje kao tvoj servis i čita njegove privatne kapsule. Ne stavljaj ih u kod ni u logove.",
      "Token samo čita i šalje kapsule. Ne može da upravlja ključevima, tokenima ni načinima prijave, pa server koji procuri ne može da preuzme nalog.",
      "Ako token procuri, opozovi ga pod Nalog, Za programere; dovoljna je prijava. Ako procure 12 reči, upotrebi Resetuj ključ pod Nalog, Ključevi.",
      "Privatne kapsule idu samo članovima koje servis prati i koji ga ne blokiraju; slanje grupi izostavlja one koji ga blokiraju.",
    ],
    limitsTitle: "Limiti",
    limits: [
      "Za sada 50 isporuka ukupno po servisnom nalogu: broji se svaki omot primaocu, a tvoje kopije su besplatne.",
      "Po tokenu: 60 zahteva u minuti, 10 000 dnevno, najviše 100 redova po stranici liste, 500 MiB preuzetih priloga dnevno.",
      "Prilozi: isti limiti kao u web aplikaciji (10 fajlova, ukupna veličina koju API javlja u /me).",
    ],
    sdkTitle: "SDK",
    sdkText: "SDK je paket @timecapsule/crypto iz Vrelis repozitorijuma (TypeScript). sealPublic i sealPrivate prave kapsule, openPublic i openPrivate ih otvaraju posle njihovog vremena, encryptFile i decryptFile rade sa prilozima, sealAddressBook i openAddressBook sa adresarom. Privatna kapsula jednom članu:",
    referenceTitle: "Referenca",
    referenceText: "Svaka ruta ispod dolazi iz OpenAPI 3.1 dokumenta koji server pravi iz sopstvenih definicija ruta; otvori ga u bilo kom OpenAPI alatu:",
    tokenLabel: "API token za Probaj (čuva se samo na ovoj stranici)",
    tokenNote: "Probaj šalje prave zahteve ovim tokenom. Za razgledanje koristi token samo za čitanje.",
    parameter: "Parametar",
    where: "Gde",
    description: "Opis",
    body: "Telo zahteva (JSON):",
    binaryBody: "Telo zahteva: šifrovani bajtovi fajla (application/octet-stream).",
    responses: "Odgovori:",
    tryIt: "Probaj",
    send: "Pošalji",
    binaryNoTry: "Otpremi iz svog servisa",
    schemas: "Šeme",
  },
  developers: {
    title: "Za programere",
    note: "API tokeni omogućavaju tvom servisu da čita i šalje kapsule preko Vrelis API-ja. Token ne može da upravlja ključevima, tokenima ni načinima prijave.",
    docsLink: "Dokumentacija API-ja",
    tokenName: "Naziv tokena (na primer server na kome radi)",
    readOnly: "Samo čitanje (bez slanja)",
    create: "Napravi token",
    shownOnce: "Kopiraj token sada i čuvaj ga u tajnim podešavanjima servisa. Prikazuje se samo jednom; Vrelis čuva samo njegov heš.",
    copied: "Kopirano",
    tokensTitle: "API tokeni",
    noTokens: "Nema tokena. Token dozvoljava tvom programu da koristi Vrelis API kao ovaj nalog, u opsegu i granicama koje mu daš.",
    readWrite: "čitanje i slanje",
    readOnlyShort: "samo čitanje",
    revoked: "opozvan",
    lastUsed: (d: string) => `poslednja upotreba ${d}`,
    neverUsed: "nije korišćen",
    revoke: "Opozovi",
  },
  keyFlow: {
    title: "Tvoj ključ",
    signature: (
      <>
        <b>Tvoj ključ</b> je tvoj glavni potpis: potpisuje svaku kapsulu koju šalješ i otvara privatne kapsule poslate tebi. Pravi se u ovom browseru;
        Vrelis ga nikad ne vidi.
      </>
    ),
    whoever: (
      <>
        Ko ima tvojih 12 reči, <b>jeste</b> ti: može da potpisuje kao ti i da čita tvoje privatne kapsule.
      </>
    ),
    createNew: "Napravi moj nov ključ",
    skip: "Preskoči za sada",
    skipNote: "Bez ključa možeš da čitaš javne kapsule, ali ne možeš ništa da pišeš niti da primaš privatne kapsule. Ključ možeš da napraviš kasnije pod Nalog, Ključevi.",
    announce: "Sledeći ekran prikazuje tvojih 12 reči. Pre nego što ga otvoriš:",
    announceChecks: [
      "proveri da niko ne vidi tvoj ekran,",
      "da ga ništa ne snima i ne deli (deljenje ekrana, kamera, alat za snimke ekrana),",
      "pripremi papir i olovku.",
    ],
    showPhrase: "Prikaži moju frazu",
  },
  keys: {
    title: "Ključevi",
    note: "Tvoj ključ potpisuje ono što šalješ i otvara privatne kapsule poslate tebi.",
    getTitle: "Preuzmi ključ na ovaj uređaj",
    getText: "Tvoj nalog ima ključ, ali ga ovaj uređaj nema. Upotrebi pristupni ključ i PIN, ili frazu za oporavak.",
    getButton: "Preuzmi moj ključ",
    activeTitle: "Tvoj trenutni ključ",
    fingerprint: "Otisak:",
    fullKey: "Ceo ključ",
    reset: "Resetuj ključ",
    resetNote: "Ako ti je fraza ukradena ili želiš novu: trenutni ključ se opoziva i praviš nov.",
    listTitle: "Svi ključevi ovog naloga",
    state: { active: "aktivan", retired: "raniji", revoked: "opozvan" },
    created: (d: string) => `napravljen ${d}`,
    lastUsed: (d: string) => `poslednja upotreba ${d}`,
    neverUsed: "nije korišćen",
    revokedAt: (d: string) => `opozvan ${d}`,
    stolenSince: (d: string) => `, ukraden od ${d}`,
    resetTitle: "Resetuj ključ",
    resetText: "Opoziv trajno gasi tvoj trenutni ključ: niko više ne može da šalje njime niti na njega. Zatim praviš nov ključ sa novom frazom, i to si od sada ti.",
    typeCurrent: "Ukucaj 12 reči svog trenutnog ključa.",
    notCurrent: "Ova fraza nije tvoj trenutni ključ.",
    notStolen: "Ništa nije ukradeno; samo želim nov ključ.",
    wasStolen: "Neko možda ima moju frazu ili ključ.",
    stolenSinceLabel: "Od kada (ostavi prazno ako ne znaš):",
    stolenNote: "Kapsule poslate od tada svi vide sa oznakom „opozvan ključ“. Bez datuma označava se sve od kada je ovaj ključ napravljen.",
    keepOld: "Zadrži stari ključ u privesku",
    keepOldNote: "Na vrelis.com kapsule poslate na stari ključ otvaraju se i dalje, bez stare fraze. U preuzetoj arhivi i offline otvaraču otvaraju se samo starom frazom, pa i nju sačuvaj.",
    ownPhraseNote: "Staru frazu čuvaš kod sebe; za otvaranje kapsula poslatih na stari ključ dodaćeš je pod Privezak.",
    revokeButton: "Opozovi moj ključ",
    revokedNow: "Tvoj stari ključ je opozvan. Sada napravi nov.",
    keyringTitle: "Privezak",
    keyringNote: "Tvoji raniji ključevi, šifrovani trenutnim ključem. Vrelis ne može da ga otvori.",
    keyringPurpose: "za otvaranje priveska",
    showKeyring: "Prikaži šta sadrži",
    keyringEmpty: "Prazan. Posle Reset key ovde se čuvaju raniji ključevi, pa se kapsule poslate na njih i dalje otvaraju na vrelis.com.",
    keyringLocked: "Tvoj privezak je još zapečaćen ranijim ključem: reset ključa je stao pre nego što ga je prebacio. Dodaj frazu tog ključa ispod; privezak prelazi na tvoj trenutni ključ sa svim što sadrži.",
    keyringLockedReset: "Novi ključ je spreman, ali privezak nije mogao da se otvori ni starim ni novim ključem, pa je ostao nepromenjen. Dodaj frazu ključa kojim je zapečaćen, pod Privezak.",
    retryKeyring: "Pokušaj ponovo da prebaciš privezak i adresar",
    later: "Kasnije",
    keyringFull: "Privezak je pun, pa stari ključ ne može da uđe u njega. Staru frazu sačuvaj kod sebe (isključi polje) i pokušaj ponovo.",
    addOld: "Dodaj raniju frazu",
    notOldKey: "Ova fraza nije raniji ključ ovog naloga.",
    addOldButton: "Dodaj u privezak",
  },
  reauth: {
    title: "Potvrdi da si to ti",
    text: (purpose: string) => `Prijavi se ponovo ${purpose}. Važi samo tvoja prijava.`,
    purposes: { createKey: "za postavljanje ovog ključa", revokeKey: "za opoziv svog ključa", createToken: "za pravljenje API tokena" },
    password: "Lozinka",
    withPassword: "Potvrdi lozinkom",
    withPasskey: "Potvrdi pristupnim ključem",
    withGoogle: "Potvrdi Google nalogom",
    popupBlocked: "Browser je blokirao Google prozor. Dozvoli iskačuće prozore za ovaj sajt i pokušaj ponovo.",
    mismatch: "Ta prijava pripada drugom nalogu.",
    closeWindow: "Gotovo. Možeš da zatvoriš ovaj prozor.",
    needStrong: "Za ovo treba tvoja lozinka ili pristupni ključ: Google ne može da potvrdi da si to ti za ovim računarom. Dodaj lozinku pod Nalog, „Kako se prijavljuješ“ (stiže ti link mejlom), pa pokušaj ponovo.",
    openSignIns: "Otvori „Kako se prijavljuješ“",
  },
  connect: {
    title: "Zahtev za povezivanje",
    request: (name: string) => `Na Vrelisu želi da se poveže s tobom: ${name}.`,
    note: "Ako prihvatiš, pratite jedno drugo, pa svako može drugom da šalje privatne kapsule. Ako odbiješ, ta osoba vidi samo da je pozivnica istekla.",
    accept: "Prihvati",
    decline: "Odbij",
    accepted: (name: string) => `Ti i ${name} sada pratite jedno drugo.`,
    declined: "Odbijeno.",
    gone: "Ovaj zahtev više nije otvoren.",
  },
  people: {
    tabs: { following: "Pratim", followers: "Prate me", blocked: "Blokirani", hidden: "Sakriveni" },
    none: {
      following: "Još nikog ne pratiš. Zaprati nekoga preko imena na kapsuli ili preko pretrage: tada toj osobi možeš da šalješ privatne kapsule, a njene javne vidiš pod Pratim.",
      followers: "Još te niko ne prati. Član koji te prati može da ti šalje privatne kapsule.",
      blocked: "Nikog ne blokiraš. Blokiraj člana iz menija na njegovom imenu ako ne želiš njegove privatne kapsule.",
      hidden: "Nikog ne sakrivaš. Sakrij člana iz menija na njegovom imenu da njegove javne kapsule nestanu iz tvoje liste Javno.",
    },
    notes: {
      following: "Privatne kapsule idu samo članovima koje pratiš.",
      followers: "Ovaj spisak vidiš samo ti.",
      blocked: "Blokiran član ne može da ti ubaci privatne kapsule u Primljeno; i dalje možeš da ga pratiš i čitaš njegove javne kapsule. Njegove ranije kapsule su u Primljeno, Blokirani.",
      hidden: "Javne kapsule sakrivenog člana nestaju iz tvog Javno, na svim uređajima. Za njega se ništa ne menja, a ti i dalje možeš da mu pišeš.",
    },
    followBack: (name: string) => `Zaprati: ${name}`,
    since: (date: string) => `od ${date}`,
  },
  contacts: {
    title: "Adresar",
    lead: "Nadimci za ljude koje pratiš i tvoje grupe (porodica, prijatelji, kolege…). Jedna osoba može biti u više grupa. Ceo adresar se šifruje tvojim ključem u ovom browser-u; mi ga samo čuvamo.",
    open: "Otvori adresar",
    purposeOpen: "da otvoriš adresar",
    purposeSave: "da sačuvaš adresar",
    unreadable: "Adresar je sačuvan ranijim ključem koji nije u privesku, pa nijedan tvoj ključ ne može da ga otvori. Ništa nije promenjeno.",
    addOldPhrase: "Dodaj frazu tog ključa pod Ključevi -> Privezak: adresar se tada otvara i prelazi na sadašnji ključ.",
    startOver: "Počni nov, prazan adresar",
    startOverConfirm: "Zameniti sačuvani adresar praznim? Grupe i imena se trajno gube, čak i ako kasnije nađeš staru frazu.",
    groups: "Grupe",
    noGroups: "Još nema grupa. Grupa je imenovan spisak ljudi koje pratiš; izaberi je u sastavljaču da jednu kapsulu pošalješ svima.",
    groupName: "Ime grupe",
    memberCount: (n: number) => `${n} ${n === 1 ? "osoba" : n >= 2 && n <= 4 ? "osobe" : "osoba"}`,
    deleteGroup: "obriši",
    confirmDelete: (name: string) => `Obrisati grupu „${name}”? Ljude i dalje pratiš.`,
    newGroup: "Nova grupa",
    newGroupHint: "npr. Porodica",
    addGroup: "Dodaj grupu",
    people: "Ljudi koje pratiš",
    nobody: "Još nikog ne pratiš.",
    toAccount: "Nalog",
    alias: "Nadimak (vidiš ga samo ti)",
    aliasHint: "npr. direktor",
    save: "Sačuvaj",
    saved: "Sačuvano",
    changedElsewhere: "Adresar je izmenjen na drugom uređaju. Otvori ga ponovo; nesačuvane izmene ovde se gube.",
  },

  admin: {
    registration: {
      title: "Registracija",
      isOpen: "Otvorena: svako može da se registruje, bez pozivnice.",
      isClosed: "Zatvorena: samo uz pozivnicu (podrazumevano u pilotu).",
      open: "Otvori registraciju",
      close: "Zatvori registraciju",
      confirmOpen: "Da li svako može da se registruje bez pozivnice?",
      confirmClose: "Ponovo dozvoliti registraciju samo uz pozivnicu?",
    },
    title: "Admin",
    newInvite: "Nova pozivnica",
    email: "Email osobe koju pozivaš",
    emailNote: "Pozivnicu može da iskoristi samo ovaj Google nalog. Sam link nije dovoljan.",
    service: "Servisni nalog (koristi API sa tokenima; adresa još ne sme da ima nalog)",
    serviceNote: "Ostali članovi vide servisni nalog kao običnog člana; samo ti i taj nalog vidite da je servisni.",
    anyone: "Umesto toga, može da se pridruži bilo ko sa linkom (prvi koji ga iskoristi)",
    days: "Važi dana",
    create: "Napravi pozivnicu",
    sendLink: "Pošalji ovaj link: ",
    invites: "Pozivnice",
    columns: { code: "Kod", for: "Za", status: "Stanje", expires: "Ističe", name: "Ime", email: "Email", joined: "Datum pridruživanja", key: "Ključ", serverCopy: "Kopija na serveru" },
    anyoneWithLink: "bilo ko sa linkom",
    usedBy: (name: string) => `iskoristio: ${name}`,
    expired: "istekla",
    claimed: "zauzeta: prijava je započeta njome",
    open: "otvorena",
    revoke: "Opozovi",
    confirmRevoke: (code: string, email: string | null) => `Da opozovemo pozivnicu ${code}${email ? ` za ${email}` : ""}? Link prestaje da radi.`,
    members: (n: number) => `Članovi (${n})`,
    states: { all: "Svi", predefined:"Predefinisan", active: "Aktivan", suspended:"Suspendovan", deactivated: "Ugašen" },
    membersNote:
      "Član se postavlja u tri koraka: prijava (datum pridruživanja), registrovan ključ, pristupni ključ i PIN štite ključ (kopija na serveru). Bez pristupnog ključa koji može da izvodi ključeve nema kopije na serveru; ključ je tada samo na tom uređaju i u frazi za oporavak.",
    deactivatedOn: (time: string) => ` · deaktiviran ${time}`,
    since: (time: string) => ` od ${time}`,
    notSetUp: "nije postavljen",
    yes: "da",
    none: "nema",
    reset: "Poništi identitet",
    reactivate: "Ponovo aktiviraj",
    deactivate: "Deaktiviraj",
    confirmReset: (name: string) =>
      `Da poništimo identitet člana ${name}?\n\nUradi ovo tek kad van aplikacije (telefonom, uživo) proveriš sa osobom ${name} da je izgubila frazu za oporavak i sve uređaje. Njen trenutni ključ se penzioniše i njene sesije se završavaju; zatim pravi nov identitet. Privatne kapsule poslate starom ključu ne mogu se otvoriti novim.`,
    askReason: (name: string) => `Zašto se poništava identitet člana ${name}? (3 do 200 znakova, ostaje u zapisniku)`,
    reasonNeeded: "Za poništavanje treba razlog od 3 do 200 znakova.",
    makeAdmin: "Učini adminom",
    removeAdmin: "Ukloni admina",
    confirmMakeAdmin: (name: string, email: string) =>
      `Da učinimo člana ${name} (${email}) adminom?\n\nAdmin vidi email adresu svakog člana i svaku pozivnicu, pravi pozivnice i linkove koje svako može da iskoristi, otvara registraciju, deaktivira članove i poništava njihov identitet. Admin ništa od toga ne može da uradi tebi ni drugom adminu. Ulogu možeš da ukloniš kad god hoćeš.`,
    confirmRemoveAdmin: (name: string) => `Da uklonimo ulogu admina članu ${name}? Pri sledećem kliku postaje običan član.`,
    askRoleReason: (name: string) => `Zašto se menja uloga člana ${name}? (3 do 200 znakova, ostaje u zapisniku)`,
    roleReasonNeeded: "Za promenu uloge treba razlog od 3 do 200 znakova.",
    confirmReactivate: (name: string, email: string) => `Da ponovo aktiviramo člana ${name}? Moći će ponovo da se prijavi sa ${email}.`,
    confirmDeactivate: (name: string, email: string) =>
      `Da deaktiviramo člana ${name} (${email})?\n\nOdmah je odjavljen svuda, ne može ponovo da se prijavi, nestaje sa spiska članova i ne može da prima privatne kapsule. Kapsule koje je već poslao ostaju. Kasnije ga možeš ponovo aktivirati.`,
  },

  attachments: {
    noImages: "ovaj browser ne može da obradi slike",
    tooBig: (name: string, size: string, left: string) => `${name} ima ${size}; za ovu kapsulu je ostalo ${left}`,
    notFound: "prilog nije pronađen",
  },

  apiErrors: {
    unsubscribe_invalid: "Ovaj link nije ispravan. Obaveštenja menjaš u Nalogu.",
    reply_unknown: "Kapsula na koju odgovaraš nema povratnicu ili ne postoji.",
    reply_not_recipient: "Na kapsulu može da odgovori samo član koji ju je primio.",
    reply_recipient: "Odgovor ide na aktivni ključ člana koji ga traži.",
    reply_unlock_mismatch: "Odgovor mora da se otvori u vreme koje povratnica navodi.",
    envelope_expired: "Vreme koje je pošiljalac odredio je prošlo; odgovor se više ne prima.",
    target_owner: "Ovo niko ne može da uradi vlasniku.",
    target_admin: "Ovo adminu može da uradi samo vlasnik.",
    role_unchanged: "Ovaj član već ima tu ulogu. Osveži spisak.",
    no_stamps: "Nemaš dovoljno maraka za ovo. Pogledaj Obračun.",
    storage_quota: "Prostor je pun. Oslobodi malo ili izaberi plan sa više prostora u Obračunu.",
    submission_conflict: "Ovi događaji su već sačuvani drugačije. Osveži stranicu i napiši kapsulu ponovo.",
    retail_only: "Servisni nalozi nemaju plan ni marke.",
    idempotency_conflict: "Taj ključ zahteva je upotrebljen za nešto drugo. Pokušaj ponovo.",
    idempotency_key_invalid: "Taj ključ zahteva nije ispravan. Osveži stranicu i pokušaj ponovo.",
    pack_limit: "Ovaj nalog već ima najviše paketa dodatnog prostora koliko je dozvoljeno.",
    checkout_open: "Plaćanje za drugi plan je još otvoreno. Završi ga ili pokušaj ponovo kad istekne.",
    price_unavailable: "Ova cena se ovde ne prodaje.",
    subscription_exists: "Već imaš pretplatu. Promeni je ili otkaži u portalu za plaćanje.",
    selling_off: "Prodaja još nije uključena: ništa ne može da se kupi.",
    mapping_invalid: (detail: string) => `Mapiranje cene nije ispravno: ${detail}`,
    billing_unavailable: "Plaćanje trenutno nije dostupno. Pokušaj kasnije.",
    portal_unavailable: "Kod posrednika za plaćanje još nema šta da se uređuje.",
    event_not_waiting: "Ovaj događaj više ne čeka: već je primenjen.",
    entitlement_not_manual: "Ovde se može završiti samo pravo koje je dato ručno.",
    billing_account_not_empty: "Ovaj login već ima obračunska prava i ne može da preuzme identitet.",
    units_invalid: "Broj maraka mora biti ceo broj od 1 do 10 000.",
    reason_invalid: "Navedi razlog od 3 do 200 znakova.",
    expiry_invalid: "Vreme isteka mora biti u budućnosti.",
    bytes_invalid: "Prostor mora biti između 1 bajta i 100 GiB.",
    plan_unknown: "Nepoznat plan.",
    upload_expired: "Otpremanje je isteklo. Dodaj fajl ponovo.",
    attachment_pending: "Fajl se još otprema. Pokušaj ponovo za trenutak.",
    billing_invariant: "Obračunska provera na serveru nije prošla. Ništa nije poslato ni naplaćeno. Prijavi problem.",
    history_kind_invalid: "Nepoznata lista.",
    payment_required: "Za ovo je potrebno nešto što nalog nema. Pogledaj Obračun.",
    invalid_request: (detail: string) => `Zahtev je odbijen: ${detail}`,
    login_required: "Tvoja sesija je završena. Prijavi se ponovo.",
    forbidden: "Ovo ti nije dozvoljeno.",
    not_found: "Nije pronađeno.",
    conflict: (detail: string) => `Ovo se kosi sa trenutnim stanjem: ${detail}`,
    too_large: "To je preveliko.",
    rate_limited: "Previše zahteva. Pokušaj kasnije.",
    unavailable: "Ova usluga trenutno nije dostupna. Pokušaj kasnije.",
    internal: "Na serveru je nešto pošlo naopako. Pokušaj ponovo; ako se ponovi, prijavi problem.",
    daily_limit: "Današnje ograničenje od 100 isporuka je dostignuto. Pokušaj sutra.",
    upload_quota: "Ograničenje otpremanja je dostignuto: 20 neiskorišćenih fajlova ili 50 MiB za 24 sata.",
    blob_too_large: "Ovi fajlovi su veći nego što tvoj plan dozvoljava u jednoj kapsuli. Pogledaj Obračun.",
    attachment_unavailable: "Neki prilog nedostaje, istekao je ili je već iskorišćen. Ukloni ga i dodaj ponovo.",
    profile_outdated: "Novija izmena profila je već sačuvana. Osveži stranicu.",
    server_copy_locked: "Kopija na našem serveru je blokirana posle 10 pogrešnih PIN-ova. Upotrebi frazu za oporavak.",
    identity_changed: "Tvoj identitet se u međuvremenu promenio. Osveži stranicu i pokušaj ponovo.",
    current_key_required: "Za promenu identiteta treba ti trenutni ključ.",
    key_owned: "Ovaj ključ već pripada drugom članu.",
    member_suspended: "Nalog je suspendovan. Obratite se administratoru: support@vrelis.com.",
    status_conflict: "Korisnik je u međuvremenu izmenjen. Osveži i pokušaj ponovo.",
    owner_only: "Samo Owner može menjati ova podešavanja.",
    target_self: "Ne možeš blokirati sopstveni nalog.",
    pilot_full: "Sva mesta u pilotu su zauzeta. Obratite se administratoru.",
    member_deactivated: "Administrator je deaktivirao ovog člana.",
    invite_required: "Za ovaj pilot potrebna je odobrena email adresa ili važeća pozivnica. Registruj se odobrenom adresom ili otvori link iz pozivnice.",
    invite_invalid: "Ova pozivnica ne važi: već je iskorišćena, istekla je ili je za drugu email adresu.",
    invalid_email: "Upiši ispravnu email adresu.",
    invalid_credentials: "Pogrešan email ili lozinka.",
    login_locked: "Previše pogrešnih lozinki. Sačekaj 15 minuta ili postavi novu lozinku.",
    mail_limit: "Danas je na ovu adresu poslato previše mejlova. Pokušaj sutra.",
    mail_unavailable: "Mejl nije mogao da se pošalje. Pokušaj kasnije.",
    link_expired: "Ovaj link je istekao ili je već iskorišćen. Zatraži novi.",
    password_exists: "Ovaj nalog već ima lozinku.",
    last_login: "Ovo je tvoj poslednji način prijave; prvo dodaj drugi.",
    passkey_refused: "Ovim pristupnim ključem ne možeš da se prijaviš ovde. Upotrebi ga kad podesiš ključ na ovom uređaju, ili se prijavi na drugi način.",
    passkey_exists: "Ovaj pristupni ključ već služi za prijavu.",
    passkey_unsupported: "Ova vrsta pristupnog ključa ne može da služi za prijavu.",
    pending_limit: "Imaš 10 kapsula koje čekaju nove članove.",
    pending_closed: "Ova kapsula više ne može da se zatraži ni odobri.",
    key_required: "Prvo podesi svoj ključ.",
    subject_required: "Kapsula mora imati naslov.",
    recipient_not_followed: "Privatna kapsula ide samo članovima koje pratiš. Prvo ih zaprati.",
    recipient_blocked: "Ovaj član ne prima tvoje privatne kapsule.",
    reauth_required: "Prijavi se ponovo da potvrdiš da si to ti.",
    reauth_mismatch: "Ta prijava pripada drugom nalogu.",
    block_limit: "Možeš da blokiraš najviše 1000 članova.",
    hide_limit: "Možeš da sakriješ najviše 1000 članova.",
    invite_limit: "Dostignut je podešeni broj otvorenih pozivnica. Povuci neku ili sačekaj da se iskoristi.",
    follow_limit: "Već pratiš najviše 500 članova.",
    address_book_changed: "Adresar je izmenjen na drugom uređaju. Otvori ga ponovo.",
    archive_refused: "Arhivirati se može samo kapsula koju vidiš i čije je vreme otvaranja prošlo.",
  },

  errors: {
    notYet: "Još ne. Vremenski signal za ovu kapsulu još nije objavljen; može da kasni nekoliko sekundi. Pokušaj uskoro ponovo.",
    beacon: "Drand vremenski signal nije dostupan. Proveri vezu i pokušaj ponovo.",
    notRecipient: "Ova kapsula je upućena nekom drugom.",
    signature: "Potpis se ne slaže. Neko je možda menjao ovu kapsulu.",
    damaged: (detail: string) => `Ova kapsula je oštećena: ${detail}`,
    keyNotUnlocked: "Tvoj ključ nije otključan na ovom uređaju.",
    keyMismatch: "Ovaj uređaj ima drugi identitet od trenutnog identiteta tvog naloga. Prvo postavi ključ na ovom uređaju.",
    keyMissing: "Tvoj ključ nije na ovom uređaju. Prvo ga postavi: link je na vrhu stranice.",
    pinBusy: "Druga radnja već čeka tvoj PIN.",
    wrongPinBlocked: "Pogrešan PIN. Kopija na serveru je sada blokirana; upotrebi frazu za oporavak.",
    wrongPin: (left: number | null) =>
      `Pogrešan PIN${left === null ? "" : `. Još ${left} ${pl(left, "pokušaj", "pokušaja", "pokušaja")} pre nego što kopija na serveru bude blokirana`}.`,
    passkeyClosed: "Prozor za pristupni ključ je zatvoren ili je isteklo vreme.",
    passkeyFailed: (detail: string) => `Greška pristupnog ključa: ${detail}`,
    passkeyUnsupported: "Ovaj browser ne može da pravi pristupne ključeve.",
    passkeyUnusable: "Ovaj browser ne može da koristi pristupne ključeve.",
    noPasskeyCreated: "Pristupni ključ nije napravljen.",
    noPasskeyUsed: "Pristupni ključ nije upotrebljen.",
    noPrf: "Ovaj pristupni ključ ne može da izvodi ključeve (nema podršku za PRF).",
    lockedDuringDownload: "Tvoj ključ je zaključan dok se preuzimanje pripremalo, pa ništa nije sačuvano. Pokušaj ponovo.",
    nothingToDownload: "Ovde nema ničega što kasnije možeš da otvoriš: samo primalac može da pročita ovu kapsulu.",
    noneDownloadable: "Nijednu od izabranih kapsula ne možeš kasnije da otvoriš.",
    exportTooMany: (max: number) => `Jedna arhiva prima najviše ${max} kapsula. Izaberi manje, a ostale preuzmi u drugoj arhivi.`,
    exportTooBig: "Ove kapsule sa fajlovima su prevelike za jednu arhivu. Izaberi manje, a ostale preuzmi u drugoj arhivi.",
  },

  archiveReadme: (count: number, date: string, includeOpened: boolean, withOpener: boolean): string[] => [
    "Vrelis vremenske kapsule",
    "========================",
    "",
    `Ova arhiva sadrži: ${capsules(count)}, preuzeto ${date}.`,
    "",
    "capsules/<id>.json   svaka kapsula onakva kakva je potpisana: javna kapsula (Nostr kind 1041) ili",
    "                     privatna kapsula upućena tebi (kind 1059). Posle vremena otvaranja fajl",
    "                     sadrži i drand signal svoje runde, pa mu drand više ne treba.",
    "blobs/<sha256>.bin   priloženi fajlovi, i dalje šifrovani; ključevi su u zapečaćenoj kapsuli.",
    includeOpened ? "opened/<id>/...      otvoren tekst je u .json fajlu; otvoreni fajlovi su ovde. NISU ŠIFROVANI." : "",
    withOpener ? "vrelis-opener.html   offline otvarač: otvori ga u browser-u i izaberi ovaj .zip fajl." : "",
    "",
    "Kako se otvara: otvori vrelis-opener.html u Chrome-u, Firefox-u ili Safari-ju i izaberi ovaj .zip.",
    "Privatnim kapsulama treba tvoja fraza za oporavak od 12 reči. Otvarač nikad ne kontaktira",
    "vrelis.com; za kapsulu bez sačuvanog signala pita javne drand releje (api.drand.sh i druge).",
    "",
    "Posle promene ključa: privatna kapsula se otvara samo frazom ključa na koji je poslata. Otvarač",
    "nema privezak, pa svaku staru frazu čuvaj uz ovu arhivu.",
    "",
    "Sve ovde koristi javne standarde sa otvorenim kodom: Nostr događaje (NIP-01, NIP-44, NIP-59),",
    "drand vremensko šifrovanje (tlock, age v1 format), BIP-39 fraze za oporavak (NIP-06).",
    "",
    includeOpened ? "Otvoren tekst i fajlovi u ovoj arhivi su nešifrovani na tvom disku: svako ko ima fajl može da ih pročita." : "",
  ],
};
